import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import IconToolTip from '../../../../common/IconToolTip';
import OverflowTip from '../../../../common/OverflowTip';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { actualValueForDropdown, ScogoID, validateNumberValue, validateStringValue, } from '../../../../utils/common';
import { TransactionStatus } from '../../PaymentController';
import FormTable from '../../../../common/Form/Table';
import DatePicker from '../../../../common/Form/DatePicker';
import Select from '../../../../common/Form/Select';
import Upload from '../../../../common/Form/FileUpload';
import TextArea from '../../../../common/Form/TextArea';
import Input from '../../../../common/Form/Input';
import { useForm, useFormContext, Controller } from 'react-hook-form';
import UserPopup from '../../../../common/Popups/User';
import { useDispatch } from 'react-redux';
import { approveServiceRequestPayment, createOrEditServiceRequestPayment, holdServiceRequestPayment, rejectServiceRequestPayment, rollbackServiceRequestPayment } from '../../../../actions/service_request';
import { IconButton } from './IconButton';
import Form from '../../../../common/Form';
import { closeModalAction, openCustomModal } from '../../../../actions/modal';
import Buttons from '../../../../common/Buttons';
import { canApproveAmountTicket, canModifyAmountTicket, canRollbackAmountTicket, MilestoneSubStatus, MilestoneType, TicketAgentRole } from '../../../Ticket/TicketController';
import { convertIntoPaisa, convertIntoRupee } from '../../../../utils/utils';
import Radio from '../../../../common/Form/Radio';
import RenderRemarksInput from '../../../../components/Inputs/RenderRemarksInput';
import { getAmount } from '../../../Wallet/components/Passbook/Card';
import { canMakeSettlement, canManageOtherOrgs } from '../../../../utils/access';
import { TransactionPurpose, TransactionCategory } from '../../../Wallet/walletController';
import { copyToClipboard } from '../../../../actions/utils';
import { checkTransactionStatus } from '../../../../actions/wallet';
import { SEND_TOAST } from '../../../../types/toast';
import { motion } from 'framer-motion';
import { userSettlement } from '../../../../actions/users';

/** Fixed widths (px) for Type → Proof columns — must match sticky `left` offsets */
const PAYOUT_LEFT_COL_WIDTHS_PX = [160, 128, 88, 112, 148, 90];

const buildPayoutStickyLeftMeta = () => {
    let left = 0;
    return PAYOUT_LEFT_COL_WIDTHS_PX.map((width) => {
        const offset = left;
        left += width;
        return { width, left: offset };
    });
};

const PAYOUT_STICKY_LEFT_META = buildPayoutStickyLeftMeta();
const PAYOUT_LEFT_TOTAL_PX = PAYOUT_LEFT_COL_WIDTHS_PX.reduce((a, b) => a + b, 0);
const PAYOUT_STATUS_WIDTH_PX = 152;
const PAYOUT_MIN_AGENT_WIDTH_PX = 144;
const PAYOUT_STICKY_HEADER_BG = '#f8f8f8';
const PAYOUT_STICKY_BODY_BG = '#ffffff';
const PAYOUT_STICKY_FOOTER_BG = 'rgb(239 246 255)';
const PAYOUT_HEADER_BORDER = '#e5e7eb';

const payoutLeftHeadStyle = (index) => ({
    position: 'sticky',
    left: PAYOUT_STICKY_LEFT_META[index].left,
    zIndex: 2 + index,
    minWidth: PAYOUT_STICKY_LEFT_META[index].width,
    maxWidth: PAYOUT_STICKY_LEFT_META[index].width,
    width: PAYOUT_STICKY_LEFT_META[index].width,
    backgroundColor: PAYOUT_STICKY_HEADER_BG,
    backgroundClip: 'padding-box',
    // Thin edge only — heavy box-shadow looked like a “broken” colour band when scrolling
    boxShadow: 'none',
    borderRight: `1px solid ${PAYOUT_HEADER_BORDER}`,
    flexShrink: 0,
});

const payoutLeftBodyStyle = (index) => {
    const isLast = index === PAYOUT_LEFT_COL_WIDTHS_PX.length - 1;
    return {
        position: 'sticky',
        left: PAYOUT_STICKY_LEFT_META[index].left,
        zIndex: 2 + index,
        minWidth: PAYOUT_STICKY_LEFT_META[index].width,
        maxWidth: PAYOUT_STICKY_LEFT_META[index].width,
        width: PAYOUT_STICKY_LEFT_META[index].width,
        backgroundColor: PAYOUT_STICKY_BODY_BG,
        boxShadow: isLast ? '4px 0 8px -4px rgba(15, 23, 42, 0.10)' : 'none',
        borderRight: isLast ? `1px solid ${PAYOUT_HEADER_BORDER}` : 'none',
        flexShrink: 0,
    };
};

const payoutStatusHeadStyle = {
    position: 'sticky',
    right: 0,
    zIndex: 10,
    minWidth: 152,
    width: 152,
    maxWidth: 152,
    backgroundColor: PAYOUT_STICKY_HEADER_BG,
    backgroundClip: 'padding-box',
    boxShadow: 'none',
    borderLeft: `1px solid ${PAYOUT_HEADER_BORDER}`,
    flexShrink: 0,
};

const payoutStatusBodyStyle = {
    position: 'sticky',
    right: 0,
    zIndex: 10,
    minWidth: 152,
    width: 152,
    maxWidth: 152,
    backgroundColor: PAYOUT_STICKY_BODY_BG,
    boxShadow: '-4px 0 8px -4px rgba(15, 23, 42, 0.08)',
    flexShrink: 0,
};

const payoutAgentColStyle = {
    minWidth: 144,
    width: 144,
    flexShrink: 0,
};

/** Middle (amount) columns — no z-index/position so they stay under sticky Proof/Status when scrolling */
const payoutAgentHeadStyle = {
    ...payoutAgentColStyle,
    backgroundColor: PAYOUT_STICKY_HEADER_BG,
    backgroundClip: 'padding-box',
};

const payoutAgentBodyStyle = {
    ...payoutAgentColStyle,
    backgroundColor: PAYOUT_STICKY_BODY_BG,
};

const payoutFooterStyle = { backgroundColor: PAYOUT_STICKY_FOOTER_BG };

/**
 * True when a column belongs to a single milestone, so amounts from other milestones must not bleed into it.
 * SP always gets one column per milestone. Warehouse Owner is milestone-scoped only when the ticket has more
 * than one WO column — EditPayout.js stamps `milestone_scoped` on the agent entry in that case — so tickets
 * with a single WO column keep the long-standing ticket-level behaviour unchanged. Cluster/PSP/Supervisor are
 * genuinely ticket-level and must stay unguarded.
 * Takes the agent/column entry (or the schema `head`, which carries the same fields).
 */
const isMilestoneScopedColumn = ({ role, milestone_id, milestone_scoped }) =>
    role === TicketAgentRole.SP || (milestone_scoped === true && milestone_id != null);

const RE_COLUMN_TINT = { head: '#d1fae5', body: '#ecfdf5' };

// Custom hook for responsive height calculation
const RenderTaxDeductionPercanetage = ({ is_kyc_verified, role }) => {
    if (is_kyc_verified === undefined) return null;
    if (!role || role === TicketAgentRole.CUSTOMER) return null;
    let text = is_kyc_verified ? '2% TDS' : '20% TDS';
    return <span>{text}</span>;
}


export const EditPayoutTable = (props) => {
    const { agents, isEditingRow, setIsEditingRow, ticketId, arrayName, serviceRequest, hasRMAMilestones = false, selectedAgentFilter, selectedStatusFilter } = props;
    const { editPayoutLoading } = useSelector((state) => state.loading);
    const { watch, setValue, control } = useFormContext();
    const watchEditPayout = watch(arrayName);
    const [selectedCategories, setSelectedCategories] = useState({});
    const [agentColWidth, setAgentColWidth] = useState(PAYOUT_MIN_AGENT_WIDTH_PX);
    const serviceRequestPayments = useSelector((state) => state.serviceRequest.serviceRequestPayments);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const isServiceRequestPaymentsLoading = useSelector((state) => state.loading.isServiceRequestPaymentsLoading);
    const IsLoggedUserIsTicketScm = serviceRequest?.agents?.find(({ role, agent }) => role === TicketAgentRole.SCM && newLoggedUserData?.user?.id === agent.id);
    // No complex height calculations needed - pure flex layout handles everything

    const memoizedCategoryList = useMemo(() => {
        if (!serviceRequest || Object.keys(serviceRequest).length === 0) return [];
        const settlement = { key: 'SETTLEMENT', label: 'SETTLEMENT', value: TransactionCategory.SETTLEMENT };
        let maxList = [
            { key: 'SERVICE', label: 'SERVICE', value: 'SERVICE' },
            { key: 'TRAVEL', label: 'TRAVEL', value: 'TRAVEL' },
            { key: 'MATERIAL', label: 'MATERIAL', value: 'MATERIAL' },
            { key: 'COURIER', label: 'COURIER', value: 'COURIER' },
            { key: 'INCENTIVE', label: 'INCENTIVE', value: 'INCENTIVE' },
            { key: 'GST', label: 'GST', value: 'GST' },
            { key: 'OVERTIME', label: 'OVERTIME', value: 'OVERTIME' },
            settlement
        ];

        const loginUserId = newLoggedUserData.user.id;
        const loginUserIsTicketSp = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.SP && loginUserId === agent.id)
        const loginUserIsTicketScm = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.SCM && loginUserId === agent.id) && !loginUserIsTicketSp;
        const loginUserIsTicketCluster = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.CLUSTER && loginUserId === agent.id);
        const loginUserIsTicketSupervisor = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.SUPERVISOR && loginUserId === agent.id);
        const loginUserIsTicketPsp = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.PSP && loginUserId === agent.id);
        const loginUserIsWarehouseOwner = serviceRequest.agents.find(({ role, agent }) => role === TicketAgentRole.WAREHOUSE_OWNER && loginUserId === agent.id);
        const loginUserIsTicketCustomer = serviceRequest.helpdesk_id === newLoggedUserData.org.id;

        if (loginUserIsTicketCustomer) {
            let list = maxList.filter(({ value }) => ['TRAVEL', 'MATERIAL', 'COURIER', 'INCENTIVE', 'SERVICE'].includes(value))
            if (canMakeSettlement(newLoggedUserData.actions)) {
                list.push(settlement);
            }
            return list;
        };

        return maxList.filter((item) => {
            if (item.value === 'TRAVEL') {
                return loginUserIsTicketScm || loginUserIsTicketCluster || loginUserIsTicketSupervisor || (loginUserIsTicketSp);
            } else if ("MATERIAL" === item.value) {
                return loginUserIsTicketSp || loginUserIsTicketScm || loginUserIsTicketSupervisor
            } else if ("COURIER" === item.value) {
                return loginUserIsTicketSp || loginUserIsWarehouseOwner || loginUserIsTicketScm || loginUserIsTicketSupervisor;
            } else if (item.value === 'INCENTIVE') {
                return loginUserIsTicketCluster || loginUserIsTicketScm || loginUserIsTicketPsp || loginUserIsTicketSupervisor;
            } else if (item.value === 'SERVICE' || item.value === 'OVERTIME') {
                return loginUserIsTicketCluster || loginUserIsTicketScm || loginUserIsTicketSupervisor;
            } else if (item.value === 'GST') {
                return loginUserIsTicketScm;
            } else if (item.value === TransactionCategory.SETTLEMENT) {
                return canMakeSettlement(newLoggedUserData.actions);
            }
            return false;
        })
    }, [serviceRequest, newLoggedUserData.user.id, newLoggedUserData.org.id, newLoggedUserData.actions]);

    const tableBodyRef = useRef();
    const scrollContainerRef = useRef();
    const prevNumRowsRef = useRef();
    const numRows = watchEditPayout?.length;
    const prevEditingRowsRef = useRef([]);

    // Scroll-awareness + dynamic agent column width
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el) return;
        const wrapper = el.parentElement;
        if (!wrapper) return;

        const updateScroll = () => {
            const { scrollLeft, scrollWidth, clientWidth } = el;
            wrapper.dataset.canScrollRight = scrollLeft + clientWidth < scrollWidth - 2 ? '1' : '0';
            wrapper.dataset.canScrollLeft = scrollLeft > 2 ? '1' : '0';
        };

        const updateSize = () => {
            if (agents.length > 0) {
                const available = el.clientWidth - PAYOUT_LEFT_TOTAL_PX - PAYOUT_STATUS_WIDTH_PX;
                setAgentColWidth(Math.max(Math.floor(available / agents.length), PAYOUT_MIN_AGENT_WIDTH_PX));
            }
            updateScroll();
        };

        updateSize();
        el.addEventListener('scroll', updateScroll, { passive: true });
        const ro = new ResizeObserver(updateSize);
        ro.observe(el);

        return () => {
            el.removeEventListener('scroll', updateScroll);
            ro.disconnect();
        };
    }, [agents.length]);

    useEffect(() => {
        if (numRows > prevNumRowsRef.current) {
            // Try to scroll the table container
            if (tableBodyRef?.current) {
                tableBodyRef.current.scroll({ top: tableBodyRef.current.scrollHeight, behavior: 'smooth' });
            }
            // Also try to scroll the main scroll container
            if (scrollContainerRef?.current) {
                scrollContainerRef.current.scroll({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
            }
            const fallbackScroll = document.querySelector('.edit-payout-scroll-container');
            if (fallbackScroll) {
                fallbackScroll.scroll({ top: fallbackScroll.scrollHeight, behavior: 'smooth' });
            }
        }
        prevNumRowsRef.current = numRows;
    }, [numRows]);

    // Effect to pre-populate form values when entering edit mode
    useEffect(() => {
        // Check if new rows entered edit mode
        const newEditingRows = isEditingRow.filter(rowIdx => !prevEditingRowsRef.current.includes(rowIdx));

        newEditingRows.forEach(rowIdx => {
            const payment = watchEditPayout[rowIdx];

            if (payment && !payment.isEditable) {
                // Pre-fill amounts for each agent role
                if (payment.roles && Object.keys(payment.roles).length > 0) {
                    // Get the actual milestone ID value
                    const milestoneId = actualValueForDropdown(payment.milestone_id);

                    agents.forEach(({ role, milestone_id: agentMilestoneId }) => {
                        const roleData = payment.roles[role];
                        if (roleData) {
                            // Match milestone for SP role specifically
                            if (role === TicketAgentRole.SP && roleData.milestone_id !== agentMilestoneId) {
                                return;
                            }

                            const amount = getAmount(roleData);
                            const fieldPath = `${arrayName}[${rowIdx}][${milestoneId}].[${role}].amount`;
                            const currentValue = watch(fieldPath);

                            // Only set if not already set to avoid infinite loops
                            if ((currentValue === undefined || currentValue === null || currentValue === '') && amount !== undefined && amount !== null) {
                                setValue(fieldPath, amount);
                            }
                        }
                    });
                }
            }
        });

        prevEditingRowsRef.current = isEditingRow;
    }, [isEditingRow, watchEditPayout, agents, arrayName, setValue, watch]);

    const isUserEditing = (idx) => isEditingRow.includes(idx);

    // Helper function to validate customer bearer amount
    const validateCustomerBearerAmount = (currentPayment, agents) => {
        // Skip validation if payment is marked as paid offline
        if (currentPayment.is_paid_offline) {
            return { isValid: true };
        }

        const additionalCostBearers = serviceRequest.metadata?.additional_cost_bearers;
        const findCustomer = agents.find((agent) => agent.role === TicketAgentRole.CUSTOMER);

        if (findCustomer && additionalCostBearers && additionalCostBearers[(actualValueForDropdown(currentPayment.category))] === TicketAgentRole.CUSTOMER) {
            const milestoneCustomerAmount = currentPayment?.[findCustomer?.milestone_id]?.[TicketAgentRole.CUSTOMER]?.amount;
            const rolesCustomerAmount = currentPayment?.roles?.[TicketAgentRole.CUSTOMER]?.amount;
            // Prefer the actively-edited milestone field (even when it's 0 / empty) so a user can't clear it to 0 and slip
            // past via the stale roles amount. Only fall back to the saved roles value when the field was never set (e.g.
            // approving an already-saved payment).
            const hasEditedAmount = milestoneCustomerAmount !== undefined && milestoneCustomerAmount !== null;
            const customerAmount = validateNumberValue(hasEditedAmount ? milestoneCustomerAmount : rolesCustomerAmount);
            if (!(customerAmount > 0)) {
                return {
                    isValid: false,
                    message: 'The customer is set as the cost bearer for this category, so a customer amount greater than 0 is required.'
                };
            }
        }
        return { isValid: true };
    };

    const milestonesList = useMemo(() => {
        if (serviceRequest?.milestones) {
            return serviceRequest?.milestones.map((m) => {
                const findAgent = serviceRequest?.agents?.find((elem) => elem.milestone_id === m.id && elem.role === TicketAgentRole.SP);
                const findFE = serviceRequest?.agents?.find((elem) => elem.milestone_id === m.id && elem.role === TicketAgentRole.FE);
                const fe_name = findFE?.agent?.display_name || findFE?.agent?.full_name;
                return {
                    key: m.id,
                    label: `${m.type}${findAgent?.agent?.display_name ? ` - SP: ${findAgent.agent.display_name}` : ''}${fe_name ? ` - FE: ${fe_name}` : ''}`,
                    value: m.id,
                    id: m.id,
                    to_show: m.type,
                    fe_name,
                }
            })
        }
        return [];
    }, [serviceRequest?.milestones, serviceRequest?.agents]);

    const hiddenRowIndices = useMemo(() => {
        if (!selectedAgentFilter && !selectedStatusFilter) return new Set();
        if (!watchEditPayout || watchEditPayout.length === 0) return new Set();

        const hidden = new Set();

        watchEditPayout.forEach((payment, index) => {
            if (payment.isEditable) return;

            let matchesAgent = true;
            let matchesStatus = true;

            if (selectedAgentFilter) {
                const { role: filterRole, milestone_id: filterMilestoneId } = selectedAgentFilter;

                if (payment.type === 'Base Payment') {
                    if (filterRole === TicketAgentRole.SP) {
                        matchesAgent = payment.milestone_id === filterMilestoneId;
                    }
                } else if (payment.roles) {
                    const roleData = payment.roles[filterRole];
                    if (!roleData) {
                        matchesAgent = false;
                    } else if (filterRole === TicketAgentRole.SP && filterMilestoneId) {
                        matchesAgent = roleData.milestone_id === filterMilestoneId || payment.milestone_id === filterMilestoneId;
                    }
                } else {
                    if (payment.ticket_agent_role !== filterRole) {
                        matchesAgent = false;
                    } else if (filterRole === TicketAgentRole.SP && filterMilestoneId) {
                        matchesAgent = payment.milestone_id === filterMilestoneId;
                    }
                }
            }

            if (selectedStatusFilter) {
                matchesStatus = payment.status === selectedStatusFilter;
            }

            if (!matchesAgent || !matchesStatus) {
                hidden.add(index);
            }
        });

        return hidden;
    }, [watchEditPayout, selectedAgentFilter, selectedStatusFilter]);

    // When the same person holds a milestone-scoped role (SP or Warehouse Owner) across two or more
    // milestones, each milestone gets its own column. Tint the entire column (header + body) of those
    // columns so it's visually clear they belong to the same person. Each duplicated agent gets its
    // own colour. Keyed by role too, so someone who is both SP and WO doesn't get falsely tinted.
    const duplicateColumnTint = useMemo(() => {
        const columnCountByRoleAndAgent = {};
        agents.forEach(({ agent, role }) => {
            if (agent?.id != null) {
                const key = `${role}_${agent.id}`;
                columnCountByRoleAndAgent[key] = (columnCountByRoleAndAgent[key] || 0) + 1;
            }
        });

        // header (slightly stronger) / body (lighter) tint pairs of the same hue
        const palette = [
            { head: '#FFEFD6', body: '#FFF8EC' }, // amber
            { head: '#DFF1E2', body: '#F1FAF3' }, // green
            { head: '#E2EAFB', body: '#F2F6FE' }, // indigo
            { head: '#F3E5FB', body: '#FAF3FE' }, // purple
            { head: '#FBE3EA', body: '#FEF2F5' }, // rose
        ];

        const tints = {};
        let paletteIndex = 0;
        Object.keys(columnCountByRoleAndAgent).forEach((key) => {
            if (columnCountByRoleAndAgent[key] > 1) {
                tints[key] = palette[paletteIndex % palette.length];
                paletteIndex += 1;
            }
        });
        return tints;
    }, [agents]);

    // FE agent id per milestone — used to show "SP/FE" in the column header when the SP and the FE of
    // that milestone are the same user.
    const feAgentIdByMilestoneId = useMemo(() => {
        const map = {};
        serviceRequest?.agents?.forEach((a) => {
            if (a.role === TicketAgentRole.FE && a.milestone_id != null) {
                map[a.milestone_id] = a.agent?.id;
            }
        });
        return map;
    }, [serviceRequest?.agents]);

    const dynamicAgentHeadStyle = {
        minWidth: PAYOUT_MIN_AGENT_WIDTH_PX,
        width: agentColWidth,
        flexShrink: 0,
        backgroundColor: PAYOUT_STICKY_HEADER_BG,
        backgroundClip: 'padding-box',
    };
    const dynamicAgentBodyStyle = {
        minWidth: PAYOUT_MIN_AGENT_WIDTH_PX,
        width: agentColWidth,
        flexShrink: 0,
        backgroundColor: PAYOUT_STICKY_BODY_BG,
    };

    let defaultSchema = {
        table: [
            {
                head: {
                    thClassName: `align-top py-2.5 pl-2`,
                    style: payoutLeftHeadStyle(0),
                    footerStyle: payoutFooterStyle,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>category</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                            </div>
                        )
                    },
                    headName: `Type`,
                },
                body: {
                    style: payoutLeftBodyStyle(0),
                    bodyClassName: `align-top py-2`,
                    render: (data) => {
                        if (data.purpose === 'TICKET_ADDITIONAL') {
                            return (
                                <div className='pl-2'>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="material-icons text-blue-600 text-font11">add_circle</span>
                                        <span className="text-font10 font-semibold text-gray-800">Additional Payment</span>
                                    </div>
                                    {data?.amount_type === 'OFFLINE' ? (
                                        <div className="flex items-center gap-1">
                                            <span className="material-icons text-orange-600 text-font08">receipt</span>
                                            <p className='text-orange-600 font-medium text-font10 truncate'>{data.reference_id}</p>
                                        </div>
                                    ) : null}
                                    {data?.created_by_details?.display_name && (
                                        <div className="bg-blue-50 px-2 rounded-md py-1">
                                            <OverflowTip
                                                someLongText={
                                                    <small className="text-blue-700 text-font08">
                                                        <p className='p-0 m-0 mb-0'>Requested by</p>
                                                        <p className='p-0 m-0 font-medium text-font10'>{`${data?.created_by_details?.display_name}`}</p>
                                                    </small>
                                                }
                                            />
                                        </div>
                                    )}

                                    <div className='text-font08 flex flex-col gap-1 text-gray-600'>
                                        {data?.created_at ? (
                                            <IconToolTip title={`Created at ${moment(data?.created_at).format('DD-MM-YYYY HH:mm:ss')}`}>
                                                <div className="flex items-center gap-1">
                                                    <span className="material-icons text-font10 font-medium">schedule</span>
                                                    <span className="font-medium text-font10">{moment(data?.created_at).format('DD-MM-YYYY HH:mm:ss')}</span>
                                                </div>
                                            </IconToolTip>
                                        ) : null}
                                    </div>
                                </div>
                            )
                        }

                        if ([TransactionPurpose.TRANSFER, TransactionPurpose.WITHDRAW].includes(data.purpose)) {
                            const status = data.status;
                            return (
                                <div className='pl-2'>
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className={`material-icons text-font12 ${status === TransactionStatus.INITIATED ? 'text-blue-500' :
                                            status === TransactionStatus.PENDING ? 'text-yellow-500' :
                                                status === TransactionStatus.SCHEDULED ? 'text-orange-500' :
                                                    status === TransactionStatus.PROCESSING ? 'text-blue-500' :
                                                        status === TransactionStatus.SUCCESS ? 'text-green-500' :
                                                            status === TransactionStatus.FAILURE ? 'text-red-500' :
                                                                status === TransactionStatus.REJECTED ? 'text-red-500' :
                                                                    'text-gray-500'
                                            }`}>
                                            account_balance
                                        </span>
                                        <span className="text-font10 font-semibold text-gray-800">{actualValueForDropdown(data.category) === TransactionCategory.SETTLEMENT ? 'Settlement' : 'Bank Transfer'}</span>
                                    </div>
                                    {actualValueForDropdown(data.category) !== TransactionCategory.SETTLEMENT ? <div className="text-font08 text-gray-600">
                                        Electronic payment processing
                                    </div> : null}
                                </div>
                            )
                        }
                        return (
                            <div className='pl-3'>
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="material-icons text-green-600 text-font12">payments</span>
                                    <span className="text-font10 font-semibold text-gray-800">Base Payment</span>
                                </div>
                                <div className="text-font08 text-gray-600">
                                    Standard service payment
                                </div>
                            </div>
                        )

                    },
                },
            },
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutLeftHeadStyle(1),
                    footerStyle: payoutFooterStyle,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>flag</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                            </div>
                        )
                    },
                    headName: `Milestone`,
                },
                body: {
                    style: payoutLeftBodyStyle(1),
                    bodyClassName: `align-top py-2`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable) {
                            return (
                                <Select
                                    name={`${arrayName}[${rowIndex}].milestone_id`}
                                    required
                                    options={milestonesList}
                                    onChange={() => {
                                        milestonesList.forEach((mile) => {
                                            setValue(`${arrayName}[${rowIndex}][${mile.id}].[${TicketAgentRole.SP}].amount`, '');
                                        })
                                    }}
                                />
                            );
                        }

                        const milestone = milestonesList.find((e) => e.id === data.milestone_id);
                        const feName = milestone?.fe_name;
                        const renderFeName = () => {
                            if (!feName) return null;
                            return (
                                <div className='text-font09 text-gray-600 flex items-center gap-1 mt-0.5'>
                                    <span className='material-icons text-font10 text-blue-500'>engineering</span>
                                    <OverflowTip someLongText={`FE: ${feName}`} />
                                </div>
                            );
                        };

                        if (data.type === 'Base Payment' && data?.milestone_label) {
                            return (
                                <div className='pl-2'>
                                    <p className='text-font10 text-scogobgsky'>{validateStringValue(data?.milestone_label).replace(/_/g, ' ')}</p>
                                    {renderFeName()}
                                </div>
                            );
                        }
                        if (milestone?.to_show) {
                            return (
                                <div className='text-font10 text-scogobgsky pl-2'>
                                    <OverflowTip someLongText={validateStringValue(milestone?.to_show).replace(/_/g, ' ')} />
                                    {renderFeName()}
                                </div>
                            );
                        }
                        return null;
                    },
                },
            },
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutLeftHeadStyle(2),
                    footerStyle: payoutFooterStyle,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2 '>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>schedule</span>
                                <OverflowTip textClass='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal' someLongText={headName}>
                                    <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                                </OverflowTip>
                            </div>
                        )
                    },
                    headName: `Scheduled On`,
                },
                body: {
                    style: payoutLeftBodyStyle(2),
                    bodyClassName: `align-top py-2`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable || isUserEditing(rowIndex)) {
                            return (
                                <DatePicker
                                    name={`${arrayName}[${rowIndex}].scheduled_at`}
                                    placeholder={'Scheduled On'}
                                    onChangeHandler={(e) => { }}
                                    minDate={new Date()}
                                    padding={'py-2'}
                                    isClearable={false}
                                    withPortal={true}
                                />
                            );
                        }
                        if (data.type === 'Base Payment') {
                            return null;
                        }
                        return (
                            <span className='text-font10 text-gray-800'>
                                {moment([TransactionPurpose.TRANSFER, TransactionPurpose.WITHDRAW].includes(data.purpose) ? data.created_at : data.scheduled_at).format('DD-MM-YYYY')}
                            </span>
                        );
                    },
                },
            },
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutLeftHeadStyle(3),
                    footerStyle: payoutFooterStyle,
                    headName: `Category`,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>label</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                                <span className='text-red-500 text-font12 shrink-0'>*</span>
                            </div>
                        )
                    },
                },
                body: {
                    style: payoutLeftBodyStyle(3),
                    bodyClassName: `align-top py-2`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable || isUserEditing(rowIndex)) {
                            return (
                                <Select
                                    name={`${arrayName}[${rowIndex}].category`}
                                    required
                                    options={memoizedCategoryList}
                                    onChange={() => {
                                        milestonesList.forEach((mile) => {
                                            setValue(`${arrayName}[${rowIndex}][${mile.id}].[${TicketAgentRole.CUSTOMER}].amount`, '');
                                        })
                                    }}
                                />
                            );
                        } else {
                            return <OverflowTip someLongText={actualValueForDropdown(data.category)} />;
                        }
                    },
                },
            },
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutLeftHeadStyle(4),
                    footerStyle: payoutFooterStyle,
                    headName: `Remark`,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>comment</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug line-clamp-3 break-words min-w-0 flex-1'>{headName}</span>
                                <span className='text-red-500 text-font12 shrink-0'>*</span>
                            </div>
                        )
                    },
                },
                body: {
                    style: payoutLeftBodyStyle(4),
                    bodyClassName: `align-top py-2`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable || isUserEditing(rowIndex)) {
                            return <TextArea type='text' name={`${arrayName}[${rowIndex}].remarks`} placeholder='Remark' />;
                        } else {
                            const isOffline = data.all_payment_data && data.all_payment_data.amount_type === 'OFFLINE';
                            return <>
                                <IconToolTip title={data.remarks || ''} placement='top'>
                                    <div className='text-font10 text-gray-800 line-clamp-3 break-words w-full cursor-default'>
                                        {data.remarks}
                                    </div>
                                </IconToolTip>
                                {isOffline && data.reference_id && <IconToolTip title={`Invoice : ${data.reference_id}`}>
                                    <p className='text-scogogray text-medium'>{data.reference_id}</p>
                                </IconToolTip>}
                            </>
                        }
                    },
                },
            },
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutLeftHeadStyle(5),
                    footerStyle: payoutFooterStyle,
                    headName: `Proof`,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>attach_file</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                            </div>
                        )
                    },
                },
                body: {
                    style: payoutLeftBodyStyle(5),
                    bodyClassName: `align-top py-2`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable || isUserEditing(rowIndex)) {
                            return (
                                <div className="flex items-center justify-center gap-2">
                                    <Upload className='w-full' name={`${arrayName}[${rowIndex}].proof`} placeholder='Proof' />
                                    {data.attachments?.length > 0 && (
                                        <div className="flex items-center gap-2">
                                            {data.attachments.map((attachment, index) => (
                                                <IconToolTip title={'Already Uploaded Attachment'}>
                                                    <a
                                                        key={index}
                                                        href={attachment}
                                                        target='_blank'
                                                        className='text-scogobgsky hover:text-scogoprimary hover:scale-110 transition-all duration-300'
                                                        rel='noreferrer'
                                                    >
                                                        <span className='material-icons text-font09' style={{ verticalAlign: 'middle', fontSize: '16px' }}>visibility</span>
                                                    </a>
                                                </IconToolTip>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        } else if (data.attachments?.length > 0) {
                            return (
                                <div className="flex gap-2">
                                    {data.attachments.map((attachment, index) => (
                                        <a key={index} href={attachment} target='_blank' className='text-scogobgsky hover:text-scogoprimary hover:scale-110 transition-all duration-300' rel='noreferrer'>
                                            <span className='material-icons text-font09' style={{ verticalAlign: 'middle', fontSize: '16px' }}>visibility</span>
                                        </a>
                                    ))}
                                </div>
                            );
                        }
                        return;
                    },
                },
            },
            ...(IsLoggedUserIsTicketScm ? [{
                head: {
                    thClassName: `w-36 truncate mb-1`,
                    headName: `Offline`,
                    render: (headName) => {
                        return (
                            <div className='flex items-center gap-1 px-2'>
                                <span className='material-icons text-blue-600 text-xs'>money_off</span>
                                <span className='text-md font-semibold text-gray-700 tracking-wide'>{headName}</span>
                            </div>
                        )
                    },
                },
                body: {
                    bodyClassName: `w-36 truncate`,
                    render: (data, { rowIndex }) => {
                        if (data.isEditable || isUserEditing(rowIndex)) {
                            return (
                                <div className="flex items-center pl-2">
                                    <Controller
                                        name={`${arrayName}[${rowIndex}].is_paid_offline`}
                                        control={control}
                                        defaultValue={false}
                                        render={({ field: { onChange, value, ref } }) => (
                                            <input
                                                type="checkbox"
                                                ref={ref}
                                                checked={!!value}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    onChange(isChecked);

                                                    // If checked, clear customer amount
                                                    if (isChecked) {
                                                        const findCustomer = agents.find((agent) => agent.role === TicketAgentRole.CUSTOMER);
                                                        if (findCustomer) {
                                                            // Clear customer amount for all milestones
                                                            milestonesList.forEach((mile) => {
                                                                setValue(`${arrayName}[${rowIndex}][${findCustomer.milestone_id}].[${TicketAgentRole.CUSTOMER}].amount`, '');
                                                            });
                                                        }
                                                    }
                                                }}
                                                className="w-5 h-5 text-blue-600 rounded cursor-pointer border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        )}
                                    />
                                </div>
                            );
                        } else {
                            if (data.type === 'Base Payment') {
                                return <></>
                            }
                            return (
                                <div className='flex items-center pl-2'>
                                    {data.is_paid_offline ? (
                                        <div className="flex items-center gap-1 text-green-600">
                                            <span className='material-icons text-sm'>check_circle</span>
                                            <span className="text-sm font-medium">Yes</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1 text-gray-400">
                                            <span className='material-icons text-sm'>cancel</span>
                                            <span className="text-sm font-medium">No</span>
                                        </div>
                                    )}
                                </div>
                            );
                        }
                    },
                },
            }] : []),
            ...agents.map(({ agent, role, label, milestone_id, milestone_ids, milestone_scoped, is_re }, idx) => {
                const isRe = Boolean(agent?.is_re ?? is_re);
                // Duplicate-agent tint takes priority; otherwise RE (employee) columns get a light green tint.
                const columnTint = (agent?.id != null ? duplicateColumnTint[`${role}_${agent.id}`] : undefined) || (isRe ? RE_COLUMN_TINT : undefined);
                const agentHeadStyle = columnTint ? { ...dynamicAgentHeadStyle, backgroundColor: columnTint.head } : dynamicAgentHeadStyle;
                const agentBodyStyle = columnTint ? { ...dynamicAgentBodyStyle, backgroundColor: columnTint.body } : dynamicAgentBodyStyle;
                // When the SP of this milestone is also the FE of the same milestone, label the column "SP/FE".
                const isSpAlsoFe = role === TicketAgentRole.SP && agent?.id != null && (
                    (milestone_id != null && feAgentIdByMilestoneId[milestone_id] === agent.id) ||
                    (Array.isArray(milestone_ids) && milestone_ids.some((mid) => feAgentIdByMilestoneId[mid] === agent.id))
                );
                const displayRole = isSpAlsoFe ? 'SP/FE' : role?.replace(/_/g, ' ');
                return {
                    head: {
                        thClassName: `align-top py-2.5`,
                        style: agentHeadStyle,
                        footerStyle: payoutFooterStyle,
                        render: () => (
                            <div className='px-2'>
                                <UserPopup
                                    userId={agent.id}
                                    anchorOrigin={{
                                        vertical: 'center',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'center',
                                    }}
                                >
                                    <div className='text-center max-w-[9rem] mx-auto'>
                                        <div className={`text-font10 font-semibold leading-snug line-clamp-3 break-words ${isRe ? 're-name-shimmer' : 'text-gray-800'}`}>
                                            <OverflowTip someLongText={`${agent.display_name}`} />
                                        </div>
                                        {role ? (
                                            <div className='text-font09 text-blue-600 font-medium mt-0.5 leading-snug line-clamp-2 break-words'>
                                                {isRe ? (
                                                    <div className='flex items-center justify-center gap-1'>
                                                        <OverflowTip someLongText={`${displayRole}`} />
                                                        <span className='shrink-0 inline-flex items-center px-1 rounded-full font-semibold leading-tight bg-emerald-100 text-emerald-700 border border-emerald-300'>RE</span>
                                                    </div>
                                                ) : <OverflowTip someLongText={`${displayRole}`} />}
                                            </div>
                                        ) : null}
                                        <div className='text-font08 text-gray-600 mt-0.5'>
                                            <RenderTaxDeductionPercanetage is_kyc_verified={agent.is_kyc_verified} role={role} />
                                        </div>
                                    </div>
                                </UserPopup>
                            </div>
                        ),
                        headName: label,
                        showTotal: true,
                        key: agent.id,
                        role,
                        milestone_id,
                        milestone_ids,
                        agent
                    },
                    body: {
                        style: agentBodyStyle,
                        bodyClassName: `align-top py-2`,
                        render: (data, { rowIndex }) => {

                            if (data.type === 'Base Payment') {
                                let amount = 0;
                                if (role === TicketAgentRole.CUSTOMER) {
                                    amount = data.customer_base_payment;
                                } else if (role === TicketAgentRole.SP) {
                                    amount = data.sp_base_payment;
                                } else if (role === TicketAgentRole.PSP) {
                                    amount = data.psp_incentive;
                                } else if (role === TicketAgentRole.CLUSTER) {
                                    amount = data.cluster_incentive;
                                } else if (role === TicketAgentRole.WAREHOUSE_OWNER) {
                                    amount = data.warehouse_incentive;
                                };

                                if (data.milestone_id !== milestone_id && role === TicketAgentRole.SP) {
                                    amount = 0;
                                }
                                return <div className='flex items-center justify-center w-full'>
                                    {amount >= 0 ? <div className='text-font10 text-green-500 font-medium'>₹ {convertIntoRupee(amount)}</div> : <></>}
                                    {amount < 0 ? <div className='text-font10 text-red-500 font-medium'>₹ {convertIntoRupee(amount)}</div> : <></>}
                                </div>
                            }

                            if (rowIndex === 0 && role === TicketAgentRole.CUSTOMER && data.purpose === TransactionPurpose.TICKET_BASE && !isNaN(data.force_added_customer_base_payment)) {
                                return (
                                    <div className='flex items-center justify-center w-full'>
                                        <div className='text-font10 text-green-500 font-medium'>₹ {convertIntoRupee(data.force_added_customer_base_payment)}</div>
                                    </div>
                                );
                            }

                            data = data?.roles ? data.roles[role] : data;
                            if (!data) return null;
                            const currenSelectedMilestone = actualValueForDropdown(watch(`${arrayName}[${rowIndex}].milestone_id`));
                            let isDisabled = TicketAgentRole.SP === role && currenSelectedMilestone !== milestone_id;
                            if (milestone_ids && milestone_ids.length > 0) {
                                isDisabled = !milestone_ids.includes(currenSelectedMilestone);
                            }

                            const selectedCategory = watch(`${arrayName}[${rowIndex}].category`);
                            const isDisabledDueToSettlement = actualValueForDropdown(selectedCategory) === TransactionCategory.SETTLEMENT && role === TicketAgentRole.CUSTOMER;
                            if (isDisabledDueToSettlement) {
                                isDisabled = true;
                            }

                            if (data.isEditable || isUserEditing(rowIndex)) {
                                const selectedCategory = watch(`${arrayName}[${rowIndex}].category`);
                                const isPaidOffline = watch(`${arrayName}[${rowIndex}].is_paid_offline`);
                                const additionalCostBearers = serviceRequest?.metadata?.additional_cost_bearers;
                                const isRequiredForCustomerBearer = role === TicketAgentRole.CUSTOMER &&
                                    additionalCostBearers &&
                                    additionalCostBearers[actualValueForDropdown(selectedCategory)] === TicketAgentRole.CUSTOMER;

                                // Disable customer input if is_paid_offline is selected
                                const isCustomerDisabled = role === TicketAgentRole.CUSTOMER && isPaidOffline;
                                const finalIsDisabled = isDisabled || isCustomerDisabled;

                                // For customer role with offline payment, show tooltip
                                if (isCustomerDisabled) {
                                    return (
                                        <div className="relative">
                                            <Input
                                                type='number'
                                                name={`${arrayName}[${rowIndex}][${milestone_id}].[${role}].amount`}
                                                placeholder=''
                                                readOnly={true}
                                                step="0.01"
                                                required={false}
                                            />
                                            {/* <IconToolTip title="Customer amount cannot be added when payment is marked as paid offline">
                                                <span className="absolute right-2 top-1/2 transform -translate-y-1/2 material-icons text-orange-500 text-sm">
                                                    info
                                                </span>
                                            </IconToolTip> */}
                                        </div>
                                    );
                                }

                                return <Input
                                    type='number'
                                    name={`${arrayName}[${rowIndex}][${milestone_id}].[${role}].amount`}
                                    placeholder='Amount'
                                    readOnly={finalIsDisabled}
                                    step="0.01"
                                    required={isRequiredForCustomerBearer && !isPaidOffline}
                                />
                            } else if (data.ticket_agent_role === role) {
                                if (milestone_ids && milestone_ids.length > 0) {
                                    if (!milestone_ids.includes(data.milestone_id)) {
                                        return null;
                                    }
                                } else if (data.ticket_agent_role === TicketAgentRole.SP && milestone_id !== data.milestone_id) {
                                    return null;
                                }

                                if (!data.ticket_agent_role && data.purpose === TransactionPurpose.TRANSFER) {
                                    data.ticket_agent_role = 'SP'
                                };

                                let amount = getAmount(data);
                                let text_color = amount >= 0 && [TransactionStatus.SUCCESS, TransactionStatus.INITIATED, TransactionStatus.PENDING, TransactionStatus.PROCESSING, TransactionStatus.SCHEDULED].includes(data.status) ? 'text-green-500' : 'text-red-500';
                                return (
                                    <div className='flex items-center justify-center w-full'>
                                        {amount >= 0 ? <div className={`text-font10 ${text_color} font-medium`}>₹ {amount}</div> : <></>}
                                        {amount < 0 ? <div className={`text-font10 ${text_color} font-medium`}>₹ {amount}</div> : <></>}
                                    </div>
                                );
                            }
                            return <></>
                        },
                    },
                }
            }),
            {
                head: {
                    thClassName: `align-top py-2.5`,
                    style: payoutStatusHeadStyle,
                    footerStyle: payoutFooterStyle,
                    headName: `Status`,
                    render: (headName) => {
                        return (
                            <div className='flex items-start gap-1.5 px-2'>
                                <span className='material-icons text-blue-600 text-font08 shrink-0 mt-0.5'>info</span>
                                <span className='text-font12 font-semibold text-gray-700 leading-snug whitespace-normal'>{headName}</span>
                            </div>
                        )
                    },
                },
                body: {
                    style: payoutStatusBodyStyle,
                    bodyClassName: `align-top py-2`,
                    render: (payment, { rowIndex, remove: removeRow }) => {
                        return (
                            <RenderAction
                                arrayName={arrayName}
                                ticketId={serviceRequest.id}
                                agents={agents}
                                idx={rowIndex}
                                removeRow={removeRow}
                                setIsEditingRow={setIsEditingRow}
                                editing={isUserEditing(rowIndex)}
                                serviceRequest={serviceRequest}
                                isBasePayment={payment.type === 'Base Payment'}
                                paymentPurpose={payment.purpose}
                                milestonesList={milestonesList}
                                memoizedCategoryList={memoizedCategoryList}
                                validateCustomerBearerAmount={validateCustomerBearerAmount}
                            />
                        );
                    },
                },
            },
        ]
    };

    useEffect(() => {
        let payments = [];
        if (serviceRequest.payments && serviceRequest.helpdesk_id === ScogoID) {
            if (serviceRequest?.milestones?.length > 0) {
                serviceRequest?.milestones.forEach((milestone, index) => {
                    if (!milestone.payments) return;
                    if (milestone.milestone_status === MilestoneSubStatus.HARD_CLOSED) return;



                    const mileStoneSp = serviceRequest?.agents?.find((ag) => ag.role === TicketAgentRole.SP && milestone.id === ag.milestone_id);

                    const findPsp = serviceRequest?.agents?.find((agent) => agent.role === TicketAgentRole.PSP);
                    let sp_base_payment = milestone?.payments?.SP_SERVICE || 0;
                    let cluster_incentive = milestone?.payments?.CLUSTER_INCENTIVE || 0;
                    // if (!findPsp && milestone?.payments?.PSP_INCENTIVE > 0 && milestone.type !== MilestoneType.RMA) {
                    //     sp_base_payment += milestone.payments.PSP_INCENTIVE
                    // }

                    if (!findPsp && milestone?.payments?.PSP_INCENTIVE > 0 && milestone.type === MilestoneType.RMA) {
                        cluster_incentive += milestone.payments.PSP_INCENTIVE;
                    };

                    if (mileStoneSp && !isNaN(mileStoneSp.ticket_payment_ratio) && sp_base_payment > 0) {
                        sp_base_payment = sp_base_payment * mileStoneSp.ticket_payment_ratio;
                    }

                    payments.push({
                        id: 'BASE_PAYMENT',
                        ticket_agent_role: serviceRequest.payments.base_payment,
                        status: serviceRequest.payments.base_payment_status,
                        isEditable: false,
                        isDeletable: false,
                        type: 'Base Payment',
                        sp_base_payment,
                        psp_incentive: milestone?.payments?.PSP_INCENTIVE || 0,
                        cluster_incentive: cluster_incentive,
                        warehouse_incentive: milestone?.payments?.WAREHOUSE_OWNER_INCENTIVE || 0,
                        customer_base_payment: index === 0 ? serviceRequest.customer_cost : 0,
                        milestone_label: milestone.type,
                        milestone_id: milestone.id,
                        purpose: TransactionPurpose.TICKET_BASE
                    });
                })
            };
        }

        const groupedPayments = [];

        if (serviceRequestPayments?.[serviceRequest.id] && serviceRequestPayments?.[serviceRequest.id].length > 0) {
            const paymentsByAgentRole = {};

            const isLoginUserIsSp = serviceRequest.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SP);
            const isLoginUserIsScm = serviceRequest.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SCM);
            const isLoginUserIsCLuster = serviceRequest.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.CLUSTER);
            const isLoginUserIsPsp = serviceRequest.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.PSP);
            const isLoginUserIsSupervisor = serviceRequest.agents.find(({ agent, role }) => agent.id === newLoggedUserData.user.id && role === TicketAgentRole.SUPERVISOR);
            const isCustomer = serviceRequest.helpdesk_id === newLoggedUserData.org.id;

            const isAdmin = isLoginUserIsScm || isLoginUserIsCLuster || isLoginUserIsPsp || isLoginUserIsSupervisor || isCustomer;

            serviceRequestPayments[serviceRequest.id] = serviceRequestPayments[serviceRequest.id].filter((payment) => {
                if (isLoginUserIsSp && !isAdmin) {
                    return isLoginUserIsSp.milestone_id === payment.milestone_id;
                }
                return true;
            }).map((elem) => {
                if (elem.reference_id === 'BASE_PAYMENT') {
                    elem.reference_id = `${elem.milestone_id}_BASE_PAYMENT`
                }
                return elem;
            })

            serviceRequestPayments[serviceRequest.id].forEach(payment => {
                if (!payment.reference_id && payment.category === TransactionCategory.SETTLEMENT) {
                    payment.reference_id = uuidv4();
                }
                if (payment.purpose === TransactionPurpose.TICKET_BASE) {
                    payment.force_added_customer_base_payment = serviceRequest.customer_cost || 0;
                    payments = payments.filter((elem) => elem.milestone_id !== payment.milestone_id)
                };

                if (paymentsByAgentRole[payment.reference_id]) {
                    if (!paymentsByAgentRole[payment.reference_id].roles) {
                        paymentsByAgentRole[payment.reference_id].roles = {};
                    }

                    let role = payment.ticket_agent_role;
                    if (!role && payment.purpose === TransactionPurpose.TRANSFER) role = 'SP'

                    if (paymentsByAgentRole[payment.reference_id].roles[role]) {
                        paymentsByAgentRole[payment.reference_id].roles[role] = payment;
                    } else {
                        paymentsByAgentRole[payment.reference_id].roles[role] = payment;
                    }
                } else {
                    let role = payment.ticket_agent_role;
                    if (!role && payment.purpose === TransactionPurpose.TRANSFER) role = 'SP'
                    paymentsByAgentRole[payment.reference_id] = {
                        ...Object.assign({}, payment),
                        roles: {
                            [role]: payment
                        }
                    };
                }
            });

            Object.entries(paymentsByAgentRole).forEach(([agentRole, paymentData]) => {
                groupedPayments.push(paymentData);
            });
        }

        if (groupedPayments && groupedPayments.length > 0) {
            groupedPayments.forEach((p, index) => {
                const categoryObj = memoizedCategoryList.find((cat) => cat.value === p.category);
                const payment = {
                    ...p,
                    category: categoryObj,
                    scheduled_at: new Date(p.scheduled_at),
                    is_paid_offline: Boolean(p.is_paid_offline)
                };

                if (p.roles && Object.keys(p.roles).length > 0) {
                    Object.keys(p.roles).forEach(role => {
                        const roleData = p.roles[role];
                        if (roleData) {
                            roleData.amount = getAmount(roleData);
                        }
                    });
                }

                // Check if this category has CUSTOMER as cost bearer and ensure CUSTOMER role exists
                const additionalCostBearers = serviceRequest.metadata?.additional_cost_bearers;
                if (additionalCostBearers && additionalCostBearers[p.category] === TicketAgentRole.CUSTOMER) {
                    // Ensure payment.roles exists
                    if (!payment.roles) {
                        payment.roles = {};
                    }

                    // If CUSTOMER role doesn't exist, create it with default values
                    if (!payment.roles[TicketAgentRole.CUSTOMER]) {
                        payment.roles[TicketAgentRole.CUSTOMER] = {
                            id: 'force_created',
                            ticket_agent_role: TicketAgentRole.CUSTOMER,
                            amount: 0,
                            credit: 0,
                            debit: 0,
                            net_credit: 0,
                            net_debit: 0,
                            milestone_id: p.milestone_id,
                            category: p.category,
                            purpose: p.purpose,
                            currency: 'INR',

                        };
                    }
                }

                payments.push(payment);
            });
        }

        const basePayments = payments.filter(
            (payment) => payment.purpose === TransactionPurpose.TICKET_BASE
        );
        const otherPayments = payments.filter(
            (payment) => payment.purpose !== TransactionPurpose.TICKET_BASE
        ).map((elem) => {
            return {
                ...elem,
                is_paid_offline: elem.amount_type === 'OFFLINE'
            }
        });

        const wallet_ids = agents.map(({ agent }) => agent.wallet_id);
        const others = otherPayments.filter((elem) => wallet_ids.includes(elem.wallet_id) || elem.ticket_agent_role === TicketAgentRole.CUSTOMER) || [];
        const sortedOthers = others
            .map((payment, index) => ({ payment, index, createdAt: Date.parse(payment.created_at) }))
            .sort((first, second) => {
                const firstHasCreatedAt = Number.isFinite(first.createdAt);
                const secondHasCreatedAt = Number.isFinite(second.createdAt);

                if (firstHasCreatedAt && secondHasCreatedAt) {
                    return first.createdAt - second.createdAt || first.index - second.index;
                }
                if (firstHasCreatedAt) return -1;
                if (secondHasCreatedAt) return 1;
                return first.index - second.index;
            })
            .map(({ payment }) => payment);
        const reorderPayments = [...basePayments, ...sortedOthers]
        setValue(arrayName, reorderPayments);
    }, [serviceRequest, serviceRequestPayments, setValue, arrayName, memoizedCategoryList, newLoggedUserData.org.id, newLoggedUserData.user.id, agents]);

    const rowGenerator = useCallback(() => {
        const row = {
            isEditable: true,
            isDeletable: true,
            type: 'Additional Payment',
            status: 'isScheduled',
            scheduled_at: canManageOtherOrgs(newLoggedUserData.actions) ? getDateDaysInFuture(7) : new Date,
            reference_id: uuidv4(),
            purpose: 'TICKET_ADDITIONAL',
        };
        if (IsLoggedUserIsTicketScm) {
            row.is_paid_offline = false;
        }

        if (serviceRequest?.milestones?.length === 1) {
            row.milestone_id = {
                key: serviceRequest?.milestones[0].id,
                label: serviceRequest?.milestones[0].type,
                value: serviceRequest?.milestones[0].id
            }
        };
        return row;
    }, [newLoggedUserData.actions, serviceRequest?.milestones]);

    const formTableRef = useRef();

    // Modern clean layout wrapper with proper flex management
    return <div className='flex flex-col h-full w-full overflow-hidden' style={{ minHeight: 0 }}>
        {/* Enhanced Loading State */}
        {isServiceRequestPaymentsLoading ? (
            <div className="absolute inset-0 flex items-center justify-center bg-white/90 backdrop-blur-sm z-50">
                <div className="flex flex-col items-center space-y-4">
                    <div className="relative">
                        <div className="w-16 h-16 border-4 border-blue-200 rounded-full animate-spin"></div>
                        <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin absolute top-0 left-0"></div>
                    </div>
                    <div className="text-center">
                        <p className="text-font12 text-blue-600 font-medium">Loading payment details...</p>
                        <p className="text-gray-500 text-font10">Please wait while we fetch the data</p>
                    </div>
                </div>
            </div>
        ) : null}

        {/* Dynamic filter styles to hide non-matching rows */}
        {hiddenRowIndices.size > 0 && (
            <style>{
                Array.from(hiddenRowIndices).map(i =>
                    `.edit-payout-body tbody tr:nth-child(${i + 1})`
                ).join(', ') + ' { display: none !important; }'
            }</style>
        )}

        {/* Table Container — single scroll container for both axes */}
        <div className='flex-1 min-h-0 overflow-hidden flex flex-col'>
            <div className="flex-1 min-h-0 bg-white rounded-xl shadow-lg border border-gray-200 flex flex-col overflow-hidden relative">
                {/* Scroll-awareness overlays (driven by data attributes set on this div) */}
                <div className="scroll-edge-right" />
                <div className="scroll-edge-left" />
                <div
                    className="scroll-nudge-left"
                    onClick={() => {
                        const el = scrollContainerRef.current;
                        if (el) el.scrollBy({ left: -200, behavior: 'smooth' });
                    }}
                >
                    <div className="scroll-nudge-left-inner flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg shadow-blue-400/40 cursor-pointer hover:scale-110 active:scale-95 transition-transform">
                        <span className="material-icons text-white" style={{ fontSize: '18px', transform: 'scaleX(-1)' }}>double_arrow</span>
                    </div>
                </div>
                <div
                    className="scroll-nudge-right"
                    onClick={() => {
                        const el = scrollContainerRef.current;
                        if (el) el.scrollBy({ left: 200, behavior: 'smooth' });
                    }}
                >
                    <div className="scroll-nudge-right-inner flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 shadow-lg shadow-blue-400/40 cursor-pointer hover:scale-110 active:scale-95 transition-transform">
                        <span className="material-icons text-white" style={{ fontSize: '18px' }}>double_arrow</span>
                    </div>
                </div>

                <style>{`
                    /* ── Scrollbar styling ── */
                    .edit-payout-scroll-container::-webkit-scrollbar {
                        width: 8px;
                        height: 8px;
                    }
                    .edit-payout-scroll-container::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 4px;
                    }
                    .edit-payout-scroll-container::-webkit-scrollbar-thumb {
                        background: #94a3b8;
                        border-radius: 4px;
                        border: 1px solid #f1f5f9;
                    }
                    .edit-payout-scroll-container::-webkit-scrollbar-thumb:hover {
                        background: #64748b;
                    }
                    .edit-payout-scroll-container::-webkit-scrollbar-corner {
                        background: #f1f5f9;
                    }

                    /* ── Flex row alignment ── */
                    .edit-payout-shell .formTable thead,
                    .edit-payout-shell .formTable tbody tr {
                        justify-content: flex-start !important;
                        align-items: stretch;
                        flex-wrap: nowrap;
                    }

                    /* ── Vertical centering for all cells ── */
                    .edit-payout-shell .formTable td,
                    .edit-payout-shell .formTable th {
                        display: flex;
                        align-items: center;
                    }
                    .edit-payout-shell .formTable td > *,
                    .edit-payout-shell .formTable th > * {
                        width: 100%;
                        min-width: 0;
                    }

                    /* ── Sticky header band ── */
                    .edit-payout-header {
                        background-color: #f8f8f8;
                    }
                    .edit-payout-shell .edit-payout-form-table thead {
                        background-color: #f8f8f8;
                    }

                    /* ── Row hover (filter-based to preserve sticky bg) ── */
                    .edit-payout-shell .formTable tbody tr {
                        transition: filter 0.15s ease;
                    }
                    .edit-payout-shell .formTable tbody tr:hover {
                        filter: brightness(0.985);
                    }

                    /* ── Scroll-awareness indicators ── */
                    .scroll-edge-right,
                    .scroll-edge-left {
                        position: absolute;
                        top: 0;
                        bottom: 8px;
                        width: 32px;
                        z-index: 25;
                        pointer-events: none;
                        opacity: 0;
                        transition: opacity 0.3s ease;
                    }
                    .scroll-edge-right {
                        right: 0;
                        background: linear-gradient(to left, rgba(148,163,184,0.12) 0%, transparent 100%);
                    }
                    .scroll-edge-left {
                        left: 0;
                        background: linear-gradient(to right, rgba(148,163,184,0.12) 0%, transparent 100%);
                    }
                    [data-can-scroll-right="1"] > .scroll-edge-right { opacity: 1; }
                    [data-can-scroll-left="1"] > .scroll-edge-left { opacity: 1; }

                    /* Nudge arrows — clickable, visible while scrollable in that direction */
                    .scroll-nudge-right,
                    .scroll-nudge-left {
                        position: absolute;
                        top: 50%;
                        transform: translateY(-50%);
                        z-index: 35;
                        opacity: 1;
                        transition: opacity 0.3s ease;
                    }
                    .scroll-nudge-right { right: 12px; }
                    .scroll-nudge-left  { left: 12px; }
                    [data-can-scroll-right="0"] > .scroll-nudge-right,
                    [data-can-scroll-left="0"]  > .scroll-nudge-left {
                        opacity: 0;
                        pointer-events: none;
                    }
                    @keyframes nudge-bounce-right {
                        0%, 100% { transform: translateX(0); }
                        50% { transform: translateX(5px); }
                    }
                    @keyframes nudge-bounce-left {
                        0%, 100% { transform: translateX(0); }
                        50% { transform: translateX(-5px); }
                    }
                    .scroll-nudge-right-inner { animation: nudge-bounce-right 1.4s ease-in-out infinite; }
                    .scroll-nudge-left-inner  { animation: nudge-bounce-left  1.4s ease-in-out infinite; }

                `}</style>

                {/*
                  Single scroll container: horizontal + vertical.
                  Header = sticky top, Totals = sticky bottom.
                  All sticky left/right columns reference this one scroll ancestor.
                */}
                <div
                    ref={scrollContainerRef}
                    className='edit-payout-shell edit-payout-scroll-container flex-1 min-h-0 overflow-auto'
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#94a3b8 #f1f5f9',
                        scrollBehavior: 'smooth',
                    }}
                >
                    <div className='w-max min-w-full min-h-full flex flex-col'>
                        {/* Sticky header */}
                        <div className="edit-payout-header sticky top-0 z-30 border-b border-gray-200 flex-shrink-0">
                            <FormTable
                                ref={formTableRef}
                                buttonDivClass='hidden'
                                formTableClass='edit-payout-form-table w-max min-w-full shadow-none border-0 rounded-none'
                                scrollToBottomOnAddRow={false}
                                isLoading={false}
                                schema={defaultSchema}
                                name={arrayName}
                                allowAdd={false}
                                rowGenerator={rowGenerator}
                                tableBodyStyle={{
                                    maxHeight: '0',
                                    overflowY: 'hidden',
                                    minHeight: '0',
                                    display: 'none'
                                }}
                                tableHeadFontSize='text-font12 font-semibold text-left text-scogogray'
                                tableHeadClass='md:flex tableHeader hidden py-0 justify-start items-stretch flex-nowrap block bg-scogof8'
                                padding='py-0'
                            />
                        </div>

                        {/* Body — grows to fill, pushes totals to bottom */}
                        <div className='edit-payout-body flex-1'>
                            <FormTable
                                buttonDivClass='hidden'
                                formTableClass='edit-payout-form-table w-max min-w-full shadow-none border-0 rounded-none'
                                scrollToBottomOnAddRow
                                isLoading={editPayoutLoading}
                                schema={defaultSchema}
                                name={arrayName}
                                allowAdd={false}
                                rowGenerator={rowGenerator}
                                rowClassName='items-stretch justify-start flex-nowrap'
                                tableHeadFontSize='text-font12 font-semibold text-left text-scogogray'
                                tableBodyStyle={{
                                    maxHeight: 'none',
                                    minHeight: 'auto',
                                    height: 'auto',
                                    overflow: 'visible'
                                }}
                                tableHeadClass='hidden'
                                padding='py-0'
                            />
                        </div>

                        {/* Sticky totals — always at bottom */}
                        <div className="edit-payout-totals sticky bottom-0 z-30 mt-auto flex-shrink-0 shadow-[0_-4px_14px_-6px_rgba(15,23,42,0.12)]">
                            <RenderTotalAmount
                                tableSchema={defaultSchema}
                                arrayName={arrayName}
                                setSelectedCategories={setSelectedCategories}
                                selectedCategories={selectedCategories}
                                rowGenerator={rowGenerator}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div >
    </div >

};

const getDateDaysInFuture = (numDays) => {
    let date = new Date();
    date.setDate(date.getDate() + numDays);
    return date;
};

export const RenderAction = ({ arrayName, ticketId, agents, idx, removeRow, setIsEditingRow, editing, serviceRequest, isBasePayment, paymentPurpose, milestonesList, memoizedCategoryList, validateCustomerBearerAmount }) => {
    const formName = `request_${idx}`;
    const { watch, setValue, getValues } = useFormContext();
    const editPayout = watch(arrayName);
    const currentPayment = editPayout[idx];
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading);
    const serviceRequestPayments = useSelector((state) => state.serviceRequest.serviceRequestPayments);
    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);
    const IsLoggedUserIsTicketScm = serviceRequest?.agents?.find(({ role, agent }) => role === TicketAgentRole.SCM && newLoggedUserData?.user?.id === agent.id);
    const onCancel = () => {
        // Clear any temporary edits when canceling
        const payment = editPayout[idx];
        if (payment && payment.roles) {
            // Reset amounts to original values
            agents.forEach(({ role, milestone_id: agentMilestoneId }) => {
                const roleData = payment.roles[role];
                if (roleData) {
                    const amount = getAmount(roleData);
                    const fieldPath = `${arrayName}[${idx}][${payment.milestone_id}].[${role}].amount`;
                    setValue(fieldPath, amount);
                }
            });
        }
        setIsEditingRow((oldIdx) => oldIdx.filter((id) => id !== idx));
    };

    const onSettleWallet = ({ amount, remark, milestone_id, id, ticket_agent_role }) => {
        const data = {
            balance_in_paisa_debit: amount,
            ticket_id: ticketId,
            remark,
            milestone_id,
            currency: 'INR',
            ticket_agent_role
        }
        dispatch(userSettlement({ data, formName, id, closeModal: false }))
    }

    const getUserTicketBalance = useCallback((ticket_agent_role) => {
        let amount = 0;
        if (ticket_agent_role && serviceRequest?.id && serviceRequestPayments && serviceRequestPayments[serviceRequest.id]) {
            const payments = serviceRequestPayments[serviceRequest.id];
            if (payments && payments.length > 0) {
                payments.forEach((payment) => {
                    if (payment.ticket_agent_role === ticket_agent_role && [TransactionStatus.SUCCESS, TransactionStatus.PENDING].includes(payment.status) && payment.purpose === TransactionPurpose.TICKET_ADDITIONAL) {
                        amount += getAmount(payment);
                    }
                    if (payment.ticket_agent_role === ticket_agent_role && [TransactionPurpose.TRANSFER, TransactionPurpose.WITHDRAW].includes(payment.purpose) && payment.status === TransactionStatus.SUCCESS) {
                        amount -= getAmount(payment);
                    }
                })
            };
        }
        return amount;
    }, [serviceRequestPayments, serviceRequest?.id]);

    const addOrEditTicketPayment = ({ processed_remark, formName, transactionsList = [] }) => {
        let transactions = [];
        let hasInvalidMilestone = false;

        if (transactionsList.length > 0) {
            transactions = transactionsList;
        } else {
            // Validate customer bearer before processing
            const validation = validateCustomerBearerAmount(currentPayment, agents);
            if (!validation.isValid) {
                dispatch({
                    type: SEND_TOAST,
                    payload: {
                        status: 'warning',
                        message: validation.message
                    }
                });
                return;
            }

            agents.forEach(({ role, agent, milestone_id }) => {
                // Skip customer role if payment is marked as paid offline
                if (role === TicketAgentRole.CUSTOMER && currentPayment.is_paid_offline) {
                    return;
                }

                const amount = validateNumberValue(currentPayment?.[milestone_id]?.[role]?.amount);

                if (!agent) return;

                const amountInPaisa = convertIntoPaisa(Math.abs(amount));

                let credit = 0;
                let debit = 0;
                let beneficiary_id = agent.id;

                if (role === 'CUSTOMER') {
                    if (amount >= 0) {
                        debit = amountInPaisa;
                    } else {
                        credit = amountInPaisa;
                    }
                    beneficiary_id = serviceRequest.org_id;
                } else {
                    if (amount >= 0) {
                        credit = amountInPaisa;
                    } else {
                        debit = amountInPaisa;
                    }
                }

                if (amount !== 0 && role === TicketAgentRole.SP && milestone_id !== actualValueForDropdown(currentPayment.milestone_id)) {
                    hasInvalidMilestone = true;
                    return;
                }

                let transaction_id = currentPayment?.roles?.[role]?.id || currentPayment.id;
                if (transaction_id === 'force_created') {
                    transaction_id = null;
                }

                transactions.push({
                    beneficiary_id,
                    beneficiary_transaction_id: transaction_id,
                    currency: "INR",
                    credit,
                    debit,
                    is_additional_user: false,
                    role,
                });
            });

            // Check if there was an invalid milestone
            if (hasInvalidMilestone) {
                dispatch({
                    type: SEND_TOAST,
                    payload: {
                        status: 'warning',
                        message: 'Invalid milestone selection. Please ensure the milestone matches the selected SP.'
                    }
                });
                return;
            }
        }
        const payload = {
            milestone_id: actualValueForDropdown(currentPayment.milestone_id),
            reference_id: currentPayment.reference_id,
            category: actualValueForDropdown(currentPayment.category),
            remarks: currentPayment.remarks,
            scheduled_date: currentPayment.scheduled_at,
            proof: currentPayment.proof,
            processed_remark: actualValueForDropdown(currentPayment.processed_remark),
            transactions,
        };
        if (IsLoggedUserIsTicketScm) {
            payload.is_paid_offline = Boolean(currentPayment.is_paid_offline);
        }

        if (actualValueForDropdown(currentPayment.category) === TransactionCategory.SETTLEMENT) {
            transactions.map((trans) => {
                if (trans.role !== TicketAgentRole.CUSTOMER) {
                    if (trans.credit <= 0 || !trans.credit) {
                        dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: `A positive amount is required for settlement for ${trans.role}.` } });
                        return;
                    }
                    onSettleWallet({ ticket_agent_role: trans.role, id: trans.beneficiary_id, amount: trans.credit, remark: currentPayment.remarks, milestone_id: actualValueForDropdown(currentPayment.milestone_id) });
                }
            })
            return;
        }

        dispatch(createOrEditServiceRequestPayment({
            payload,
            formName,
            ticketId,
            milestone_id: actualValueForDropdown(currentPayment.milestone_id),
            onSuccess: () => {
                onCancel(idx);
            },
        }));
    };



    const openRemarkPopup = ({ formName, onClick }) => {
        dispatch(
            openCustomModal({
                heading: 'Remark',
                modalHeight: 'auto',
                modalWidth: '40rem',
                modalComponent: <RenderRemarkForm formName={formName} onClick={onClick} />
            })
        )
    }

    const onRollbackClick = () => {
        const list = editPayout[idx];
        let transactions = [];
        Object.entries(list.roles).forEach(([key, value]) => {
            if (value.id !== 'force_created') {
                transactions.push({ id: value.id, scheduled_date: value.scheduled_at })
            }
        })
        openRemarkPopup({
            formName,
            onClick: ({ processed_remark }) => {
                dispatch(rollbackServiceRequestPayment({
                    payload: {
                        processed_remark: processed_remark?.label ? processed_remark?.label : processed_remark,
                        transactions
                    },
                    formName,
                    ticketId,
                    milestone_id: actualValueForDropdown(currentPayment.milestone_id)
                }));
            }
        });
    }

    const onApproveClick = () => {
        // Validate customer bearer amount before approving
        const validation = validateCustomerBearerAmount(currentPayment, agents);
        if (!validation.isValid) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: validation.message
                }
            });
            // Open edit mode to allow user to fix the issue
            onEditClick();
            return;
        }

        // Validate that at least one amount exists in roles
        const list = editPayout[idx];
        if (!list.roles || Object.keys(list.roles).length === 0) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: 'Cannot approve payment without any amounts.'
                }
            });
            return;
        }

        // Check if any role has a valid amount
        const hasValidAmount = Object.values(list.roles).some(roleData => {
            const amount = getAmount(roleData);
            return amount !== undefined && amount !== null && amount !== 0;
        });

        if (!hasValidAmount) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: 'Cannot approve payment without any valid amounts.'
                }
            });
            return;
        }

        let transactions = [];
        Object.entries(list.roles).forEach(([key, value]) => {
            transactions.push({ id: value.id, scheduled_date: value.scheduled_at })
        })
        openRemarkPopup({
            formName,
            onClick: ({ processed_remark }) => {
                dispatch(approveServiceRequestPayment({
                    payload: {
                        processed_remark: processed_remark?.label ? processed_remark?.label : processed_remark,
                        transactions: transactions
                    },
                    formName,
                    ticketId,
                    milestone_id: actualValueForDropdown(currentPayment.milestone_id)
                }));
            }
        });
    }

    const onRejectClick = () => {
        const list = editPayout[idx];
        let transactions = [];
        Object.entries(list.roles).forEach(([key, value]) => {
            if (value.id !== 'force_created') {
                transactions.push({ id: value.id, scheduled_date: value.scheduled_at })
            }
        })
        openRemarkPopup({
            formName,
            onClick: ({ processed_remark }) => {
                dispatch(rejectServiceRequestPayment({
                    payload: {
                        processed_remark: processed_remark?.label ? processed_remark?.label : processed_remark,
                        transactions
                    },
                    formName,
                    ticketId,
                    milestone_id: actualValueForDropdown(currentPayment.milestone_id)
                }));
            }
        });
    };

    const refreshTransactionStatus = () => {
        dispatch(checkTransactionStatus({ id: currentPayment?.id, formName: `transaction_status_${currentPayment?.id}`, source: 'additional_payment_card', ticketId: serviceRequest.id }));
    }

    const onEditClick = () => {
        const payment = editPayout[idx];

        if (payment) {
            if (payment.milestone_id) {
                const milestoneObj = milestonesList?.find(m => m.id === payment.milestone_id);
                if (milestoneObj) {
                    setValue(`${arrayName}[${idx}].milestone_id`, milestoneObj);
                }
            }

            if (payment?.roles?.[TicketAgentRole.CUSTOMER]) {
                const findC = agents.find((agent) => agent.role === TicketAgentRole.CUSTOMER);
                if (findC) {
                    setValue(`${arrayName}[${idx}].${findC.milestone_id}.[${TicketAgentRole.CUSTOMER}].amount`, validateNumberValue(payment.roles[TicketAgentRole.CUSTOMER].amount));
                }
                setValue(`${arrayName}[${idx}].${payment.milestone_id}.[${TicketAgentRole.CUSTOMER}].amount`, validateNumberValue(payment.roles[TicketAgentRole.CUSTOMER].amount));
            }

            if (payment.scheduled_at) {
                setValue(`${arrayName}[${idx}].scheduled_at`, new Date(payment.scheduled_at));
            }

            if (payment.category) {
                const categoryObj = memoizedCategoryList?.find(c => c.value === actualValueForDropdown(payment.category));
                if (categoryObj) {
                    setValue(`${arrayName}[${idx}].category`, categoryObj);
                }
            }

            if (payment.remarks) {
                setValue(`${arrayName}[${idx}].remarks`, payment.remarks);
            }

            if (payment.attachments && payment.attachments.length > 0) {
                // setValue(`${arrayName}[${idx}].proof`, payment.attachments[0]);
            }

            if (payment.is_paid_offline !== undefined) {
                setValue(`${arrayName}[${idx}].is_paid_offline`, Boolean(payment.is_paid_offline));
            }
        }

        setIsEditingRow((oldIdx) => [...oldIdx, idx]);
    }

    const onHoldClick = () => {
        const list = editPayout[idx];
        let transactions = [];
        Object.entries(list.roles).forEach(([key, value]) => {
            if (value.id !== 'force_created') {
                transactions.push({ id: value.id, scheduled_date: value.scheduled_at })
            }
        })
        openRemarkPopup({
            formName,
            onClick: ({ processed_remark }) => {
                dispatch(holdServiceRequestPayment({
                    payload: {
                        remark: processed_remark?.label ? processed_remark?.label : processed_remark,
                        transactions
                    },
                    formName,
                    ticketId,
                    milestone_id: actualValueForDropdown(currentPayment.milestone_id)
                }));
            }
        });
    }

    const onResumeClick = () => {
        // Validate customer bearer amount before resuming
        const validation = validateCustomerBearerAmount(currentPayment, agents);
        if (!validation.isValid) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: validation.message
                }
            });
            onEditClick();
            return;
        }

        const list = editPayout[idx];
        let transactionsList = [];
        if (list.roles && Object.keys(list.roles).length > 0) {
            Object.entries(list.roles).forEach(([role, roleData]) => {
                let beneficiary_id = '';
                if (role === TicketAgentRole.SP) {
                    beneficiary_id = agents.find((agent) => {
                        if (agent.role !== role) return false;
                        if (agent.milestone_ids && agent.milestone_ids.length > 0) {
                            return agent.milestone_ids.includes(roleData.milestone_id);
                        }
                        return agent.milestone_id === roleData.milestone_id;
                    })?.agent.id;
                } else {
                    beneficiary_id = agents.find((agent) => agent.role === role)?.agent.id;
                }

                if (!beneficiary_id) {
                    console.log('Not Found');
                    beneficiary_id = roleData.user_id;
                }

                if (roleData && roleData.id) {
                    transactionsList.push({
                        beneficiary_id: beneficiary_id,
                        beneficiary_transaction_id: roleData.id,
                        currency: roleData.currency || "INR",
                        credit: roleData.credit || 0,
                        debit: roleData.debit || 0,
                        is_additional_user: roleData.is_additional_user || false,
                        role: role,
                    });
                }
            });
        }

        addOrEditTicketPayment({ formName, transactionsList })
    }

    if (formButtonLoading[formName]) {
        return (
            <div className="flex items-center justify-center">
                <motion.div
                    className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full border border-blue-200"
                    animate={{ scale: [1, 1.05, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                >
                    <motion.span
                        className="material-icons text-blue-600 text-font12"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                        sync
                    </motion.span>
                    <span className="text-blue-700 font-medium text-font10">Processing...</span>
                </motion.div>
            </div>
        );
    }
    if (!currentPayment) return null;

    const showRequestButton = currentPayment.isEditable && !editing;
    const showDeleteButton = currentPayment.isDeletable && !editing;
    const showEditButton = paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && [TransactionStatus.PENDING, TransactionStatus.SCHEDULED].includes(currentPayment.status) && !currentPayment.isEditable && !editing;
    const showRollbackButton = paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && currentPayment.status === TransactionStatus.SUCCESS && !currentPayment.isEditable && !editing;
    const showSaveButton = editing;
    const showCancelButton = editing;
    const showApproveButton = canApproveAmountTicket(serviceRequest) && [TransactionStatus.PENDING].includes(currentPayment.status) && paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && !currentPayment.isEditable && !editing;
    const showRejectButton = [TransactionStatus.PENDING, TransactionStatus.SCHEDULED, TransactionStatus.HOLD].includes(currentPayment.status) && paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && !currentPayment.isEditable && !editing;
    const showHoldButton = canApproveAmountTicket(serviceRequest) && [TransactionStatus.PENDING, TransactionStatus.SCHEDULED].includes(currentPayment.status) && paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && !currentPayment.isEditable && !editing;
    const showResumeButton = canApproveAmountTicket(serviceRequest) && [TransactionStatus.HOLD].includes(currentPayment.status) && paymentPurpose === TransactionPurpose.TICKET_ADDITIONAL && !currentPayment.isEditable && !editing;


    const onSave = () => {
        // Validate customer bearer amount before saving
        const validation = validateCustomerBearerAmount(currentPayment, agents);
        if (!validation.isValid) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: validation.message
                }
            });
            return;
        }

        // Validate that at least one amount is entered - check all milestone keys
        let hasAnyAmount = false;
        const allMilestoneIds = agents.map(a => a.milestone_id).filter(Boolean);
        const isPaidOffline = currentPayment.is_paid_offline;

        for (const milestoneId of allMilestoneIds) {
            const milestoneObj = currentPayment?.[milestoneId] || {};
            hasAnyAmount = Object.entries(milestoneObj).some(([role, value]) => {
                // Skip customer role if payment is offline
                if (role === TicketAgentRole.CUSTOMER && isPaidOffline) {
                    return false;
                }
                const amount = value?.amount;
                return !!amount && !isNaN(amount) && Number(amount) !== 0;
            });
            if (hasAnyAmount) break;
        }

        if (!hasAnyAmount) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: 'Please enter at least one amount before saving.'
                }
            });
            return;
        }

        // Validate required fields
        if (!actualValueForDropdown(currentPayment.category) || !currentPayment.remarks) {
            dispatch({
                type: SEND_TOAST,
                payload: {
                    status: 'warning',
                    message: 'Category and Remark are required.'
                }
            });
            return;
        }

        // Validate material category proof requirement - check all milestone keys
        let isOtherThanCustomerAmountSelected = false;

        for (const milestoneId of allMilestoneIds) {
            const milestoneObj = currentPayment?.[milestoneId] || {};
            isOtherThanCustomerAmountSelected = Object.entries(milestoneObj).some(([role, value]) => {
                // Only check non-customer roles
                if (role === TicketAgentRole.CUSTOMER) {
                    return false;
                }
                const amount = value?.amount;
                return !!amount && !isNaN(amount) && Number(amount) !== 0;
            });
            if (isOtherThanCustomerAmountSelected) break;
        }

        if (actualValueForDropdown(currentPayment.category) === TransactionCategory.MATERIAL && isOtherThanCustomerAmountSelected) {
            const hasProof = currentPayment.proof && (!Array.isArray(currentPayment.proof) || currentPayment.proof.length > 0);
            const hasAttachments = currentPayment.attachments && Array.isArray(currentPayment.attachments) && currentPayment.attachments.length > 0;
            if (!hasProof && !hasAttachments) {
                dispatch({
                    type: SEND_TOAST,
                    payload: {
                        status: 'warning',
                        message: 'Proof is required for Material category.'
                    }
                });
                return;
            }
        }

        addOrEditTicketPayment({ formName })
    };


    const copyTransactionInvoice = () => {
        let cp = currentPayment;
        let role = cp.ticket_agent_role;
        if (!role && cp.purpose === TransactionPurpose.TRANSFER) {
            role = 'SP'
        }
        if (cp?.roles && cp.roles[role]) {
            cp = cp.roles[role]
        }

        let content = '';
        let sender = 'Scogo';
        if (newLoggedUserData.org.id === serviceRequest.helpdesk_id) {
            sender = newLoggedUserData.org.name;
        }

        let time = cp.tx_time;
        let beneficiary_name = '';
        if (agents.length > 0 && cp.ticket_agent_role && cp.milestone_id) {
            const agent = agents.find((agent) => agent.role === cp.ticket_agent_role && agent.milestone_id === cp.milestone_id);
            if (agent) {
                beneficiary_name = agent.agent.display_name;
            }
        }

        content = '';
        content += `${sender}\n`;
        if (beneficiary_name) content += `Beneficiary Name: ${beneficiary_name}\n`;
        content += `Amount: ${getAmount(cp)}\n`;
        if (cp.transfer_type) content += `Mode of Payment: ${cp.transfer_type?.replace(/_/g, ' ')}\n`;
        content += `${sender} Transaction No: ${cp.id}\n`;
        if (serviceRequest.ticket_number) content += `Ticket Number: ${serviceRequest.ticket_number}\n`;
        content += `Transaction Time: ${moment(time).format('DD-MM-YYYY HH:mm:ss')}\n`;
        if (cp.remarks) {
            content += `Remark: ${cp.remarks}`;
        }
        dispatch(copyToClipboard({ data: content }))
    }

    return (
        <div className="space-y-2">
            <div className='flex items-center gap-2 flex-wrap'>
                {showRequestButton && (
                    <motion.div
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Buttons
                            buttonClass={`text-font09 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 px-3 py-1.5 shadow-lg hover:shadow-xl transition-all duration-200 font-semibold border border-blue-400`}
                            onClick={(event) => {
                                // Validate required fields first
                                if (!actualValueForDropdown(currentPayment.category) || !currentPayment.remarks) {
                                    dispatch({
                                        type: SEND_TOAST,
                                        payload: {
                                            status: 'warning',
                                            message: 'Category and Remark are required.'
                                        }
                                    });
                                    return;
                                }

                                // Validate that at least one amount is entered
                                const findCustomer = agents.find((agent) => agent.role === TicketAgentRole.CUSTOMER);
                                const isPaidOffline = currentPayment.is_paid_offline;

                                // Only check customer amount if not paid offline
                                const customerAmount = !isPaidOffline ? currentPayment?.[findCustomer?.milestone_id]?.[TicketAgentRole.CUSTOMER]?.amount : null;

                                // Check amounts across all milestone keys (agents can have different milestone_ids)
                                let hasAnyAmount = !!customerAmount && !isNaN(customerAmount) && Number(customerAmount) !== 0;

                                if (!hasAnyAmount) {
                                    // Check all milestone keys in currentPayment
                                    const allMilestoneIds = agents.map(a => a.milestone_id).filter(Boolean);
                                    for (const milestoneId of allMilestoneIds) {
                                        const milestoneObj = currentPayment?.[milestoneId] || {};
                                        hasAnyAmount = Object.entries(milestoneObj).some(([role, value]) => {
                                            // Skip customer role if payment is offline
                                            if (role === TicketAgentRole.CUSTOMER && isPaidOffline) {
                                                return false;
                                            }
                                            const amount = value?.amount;
                                            return !!amount && !isNaN(amount) && Number(amount) !== 0;
                                        });
                                        if (hasAnyAmount) break;
                                    }
                                }

                                if (!hasAnyAmount) {
                                    dispatch({
                                        type: SEND_TOAST,
                                        payload: {
                                            status: 'warning',
                                            message: 'Please enter at least one amount before requesting payment.'
                                        }
                                    });
                                    return;
                                }

                                // Validate customer bearer amount
                                const validation = validateCustomerBearerAmount(currentPayment, agents);
                                if (!validation.isValid) {
                                    dispatch({
                                        type: SEND_TOAST,
                                        payload: {
                                            status: 'warning',
                                            message: validation.message
                                        }
                                    });
                                    return;
                                }

                                // Check if other than customer amounts are selected for material proof validation
                                let isOtherThanCustomerAmountSelected = false;

                                const allMilestoneIds = agents.map(a => a.milestone_id).filter(Boolean);
                                for (const milestoneId of allMilestoneIds) {
                                    const milestoneObj = currentPayment?.[milestoneId] || {};
                                    isOtherThanCustomerAmountSelected = Object.entries(milestoneObj).some(([role, value]) => {
                                        // Only check non-customer roles
                                        if (role === TicketAgentRole.CUSTOMER) {
                                            return false;
                                        }
                                        const amount = value?.amount;
                                        return !!amount && !isNaN(amount) && Number(amount) !== 0;
                                    });
                                    if (isOtherThanCustomerAmountSelected) break;
                                }

                                if (actualValueForDropdown(currentPayment.category) === TransactionCategory.MATERIAL && isOtherThanCustomerAmountSelected) {
                                    if (!currentPayment.proof || (Array.isArray(currentPayment.proof) && currentPayment.proof.length === 0)) {
                                        dispatch({
                                            type: SEND_TOAST,
                                            payload: {
                                                status: 'warning',
                                                message: 'Proof is required for Material category.'
                                            }
                                        });
                                        return;
                                    }
                                }

                                if (!actualValueForDropdown(currentPayment.category) || !currentPayment.remarks) {
                                    dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: 'Category and Remark are required' } });
                                    return;
                                }

                                if (actualValueForDropdown(currentPayment.category) === TransactionCategory.SETTLEMENT && actualValueForDropdown(currentPayment.milestone_id)) {
                                    const selectedSettlements = currentPayment[actualValueForDropdown(currentPayment.milestone_id)];

                                    let anyInvalid = false;
                                    Object.entries(selectedSettlements).forEach(([role, { amount }]) => {
                                        const userTicketBalance = getUserTicketBalance(role);
                                        if (amount && parseFloat(amount) > 0 && userTicketBalance < parseFloat(amount)) {
                                            dispatch({ type: SEND_TOAST, payload: { status: 'warning', message: 'Settlement amount should be less than the user ticket balance' } });
                                            anyInvalid = true;
                                        }
                                    });

                                    if (anyInvalid) return;
                                }

                                addOrEditTicketPayment({ event, idx, formName })
                            }}
                            loading={formButtonLoading[formName]}
                            disabledClass='text-font09 rounded-lg px-3 py-1.5 flex items-center justify-center relative cursor-not-allowed border-gray-300 bg-gray-200 text-gray-500'
                        >
                            <div className="flex items-center gap-1">
                                <span className="material-icons text-font08">send</span>
                                <span>Request</span>
                            </div>
                        </Buttons>
                    </motion.div>
                )}

                <div className='flex items-start gap-1'>
                    {showApproveButton ? (
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                        >
                            <IconButton
                                loader={formButtonLoading[formName]}
                                onClick={(e) => {
                                    e.preventDefault();
                                    onApproveClick();
                                }}
                                onHoverTitle="Approve Payment"
                                iconName={'check_circle'}
                                backgroundColor={'bg-gradient-to-r from-green-400 to-green-500 text-white border border-green-300 shadow-lg hover:shadow-xl hover:from-green-500 hover:to-green-600'}
                                loaderSize='12'
                            />
                        </motion.div>
                    ) : null}

                    {showRejectButton ? (
                        <motion.div
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            transition={{ duration: 0.2 }}
                        >
                            <IconButton
                                onClick={(e) => {
                                    e.preventDefault();
                                    onRejectClick();
                                }}
                                onHoverTitle={'Reject Payment'}
                                iconName={'cancel'}
                                backgroundColor={'bg-gradient-to-r from-red-400 to-red-500 text-white border border-red-300 shadow-lg hover:shadow-xl hover:from-red-500 hover:to-red-600'}
                                loaderSize='12'
                            />
                        </motion.div>
                    ) : null}
                </div>

                {showHoldButton ? (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onHoldClick();
                            }}
                            onHoverTitle={'Hold Payment'}
                            iconName={'pause_circle_outline'}
                            backgroundColor={'bg-gradient-to-r from-orange-400 to-orange-500 text-white border border-orange-300 shadow-lg hover:shadow-xl hover:from-orange-500 hover:to-orange-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                ) : null}

                {showResumeButton ? (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onResumeClick();
                            }}
                            onHoverTitle={'Resume Payment'}
                            iconName={'play_circle_outline'}
                            backgroundColor={'bg-gradient-to-r from-blue-400 to-blue-500 text-white border border-blue-300 shadow-lg hover:shadow-xl hover:from-blue-500 hover:to-blue-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                ) : null}

                {showRollbackButton && canRollbackAmountTicket(serviceRequest) && (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onRollbackClick();
                            }}
                            onHoverTitle={'Rollback Payment'}
                            iconName={'undo'}
                            backgroundColor={'bg-gradient-to-r from-amber-400 to-amber-500 text-white border border-amber-300 shadow-lg hover:shadow-xl hover:from-amber-500 hover:to-amber-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                )}

                {showEditButton && canModifyAmountTicket(serviceRequest) && (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onEditClick();
                            }}
                            iconName="edit"
                            onHoverTitle="Edit Payment"
                            backgroundColor={'bg-gradient-to-r from-indigo-400 to-indigo-500 text-white border border-indigo-300 shadow-lg hover:shadow-xl hover:from-indigo-500 hover:to-indigo-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                )}

                {showDeleteButton && (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                const currentValues = getValues(arrayName) || [];
                                const newValues = [...currentValues];
                                newValues.splice(idx, 1);
                                setValue(arrayName, newValues);
                            }}
                            iconName="delete_outline"
                            onHoverTitle="Delete Payment"
                            backgroundColor={'bg-gradient-to-r from-red-400 to-red-500 text-white border border-red-300 shadow-lg hover:shadow-xl hover:from-red-500 hover:to-red-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                )}

                {showSaveButton && (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onSave();
                            }}
                            iconName="save"
                            onHoverTitle="Save Changes"
                            backgroundColor={'bg-gradient-to-r from-green-400 to-green-500 text-white border border-green-300 shadow-lg hover:shadow-xl hover:from-green-500 hover:to-green-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                )}

                {showCancelButton && (
                    <motion.div
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        transition={{ duration: 0.2 }}
                    >
                        <IconButton
                            onClick={(e) => {
                                e.preventDefault();
                                onCancel();
                            }}
                            iconName="close"
                            onHoverTitle="Cancel Changes"
                            backgroundColor={'bg-gradient-to-r from-gray-400 to-gray-500 text-white border border-gray-300 shadow-lg hover:shadow-xl hover:from-gray-500 hover:to-gray-600'}
                            loaderSize='12'
                        />
                    </motion.div>
                )}


            </div>
            {currentPayment?.status && !currentPayment?.isEditable && (
                <div className="w-full rounded-lg px-1 py-0">
                    <div className="flex items-center gap-2">
                        <RenderStatus status={currentPayment.status} />

                        <div className="flex items-center gap-2">
                            {currentPayment.purpose === TransactionPurpose.TRANSFER && [TransactionStatus.INITIATED, TransactionStatus.SCHEDULED, TransactionStatus.PENDING, TransactionStatus.PROCESSING].includes(currentPayment.status) ? (
                                <motion.button
                                    onClick={refreshTransactionStatus}
                                    className={`flex items-center gap-1 px-2 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-md transition-all duration-200 ${formButtonLoading[`transaction_status_${currentPayment.id}`] ? 'animate-pulse' : ''}`}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                >
                                    <IconToolTip placement={'bottom'} title={`Refresh Transaction Status`}>
                                        <motion.span
                                            className={`material-icons text-font08 ${formButtonLoading[`transaction_status_${currentPayment.id}`] ? 'animate-spin' : ''}`}
                                            animate={formButtonLoading[`transaction_status_${currentPayment.id}`] ? { rotate: 360 } : {}}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        >
                                            refresh
                                        </motion.span>
                                    </IconToolTip>
                                </motion.button>
                            ) : null}

                            {currentPayment.purpose === TransactionPurpose.TRANSFER ? (
                                <motion.button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        copyTransactionInvoice();
                                    }}
                                    className='flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 text-green-700 rounded-md overflow-hidden'
                                >
                                    <IconToolTip placement={'bottom'} title={`Copy Transaction Details`}>
                                        <motion.span
                                            className={`material-icons text-font08 ${formButtonLoading[`transaction_status_${currentPayment.id}`] ? 'animate-spin' : ''}`}
                                            animate={formButtonLoading[`transaction_status_${currentPayment.id}`] ? { rotate: 360 } : {}}
                                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                        >
                                            content_copy
                                        </motion.span>
                                    </IconToolTip>
                                </motion.button>
                            ) : null}
                        </div>
                    </div>
                </div>
            )
            }

            {
                !isBasePayment && currentPayment?.processed_by_details && [TransactionStatus.SUCCESS, TransactionStatus.REJECTED, TransactionStatus.HOLD, TransactionStatus.PROCESSING, TransactionStatus.SCHEDULED, TransactionStatus.INITIATED].includes(currentPayment?.status) && (
                    <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-1 border border-blue-200'>
                        <div className="flex items-center gap-1">
                            <span className='material-icons text-blue-600 text-font10'>person</span>
                            <h4 className='text-font10 font-semibold text-blue-800'>Processing Details</h4>
                        </div>
                        <div className="text-gray-700">
                            <RenderProcessedBy
                                processed_by_details={currentPayment?.processed_by_details}
                                processed_at={currentPayment?.processed_at}
                            />
                        </div>
                    </div>
                )
            }
            {
                !isBasePayment && !currentPayment?.processed_by_details && currentPayment?.created_by_details && [TransactionStatus.SUCCESS, TransactionStatus.REJECTED, TransactionStatus.HOLD, TransactionStatus.PROCESSING, TransactionStatus.SCHEDULED, TransactionStatus.INITIATED] && (
                    <div className='bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-1 border border-blue-200'>
                        <div className="flex items-center gap-1">
                            <span className='material-icons text-blue-600 text-font10'>person</span>
                            <h4 className='text-font10 font-semibold text-blue-800'>Processing Details</h4>
                        </div>
                        <div className="text-gray-700">
                            <RenderProcessedBy
                                processed_by_details={currentPayment?.created_by_details}
                                processed_at={currentPayment?.created_at}
                            />
                        </div>
                    </div>
                )
            }
        </div >
    )
}


const RenderProcessedBy = ({ processed_by_details, processed_at }) => {
    return (
        <small className='flex flex-col'>
            {processed_by_details ? <b>{`${processed_by_details?.display_name}`}</b> : <></>}
            {processed_at ? <b>{`${moment(processed_at).format('DD-MM-YYYY HH:mm:ss')}`}</b> : <></>}
        </small>
    )
}



export const RenderStatus = ({ status }) => {
    return (
        <div className={`select-none max-w-max text-font10 px-2 py-0.5 rounded-lg text-gray-800 font-medium ${status === TransactionStatus.INITIATED ? 'bg-blue-200' :
            status === TransactionStatus.PENDING ? 'bg-yellow-200' :
                status === TransactionStatus.SCHEDULED ? 'bg-orange-200' :
                    status === TransactionStatus.PROCESSING ? 'bg-blue-300' :
                        status === TransactionStatus.SUCCESS ? 'bg-green-200' :
                            status === TransactionStatus.FAILURE ? 'bg-red-300' :
                                status === TransactionStatus.REJECTED ? 'bg-red-200' :
                                    status === TransactionStatus.HOLD ? 'bg-orange-200' :
                                        'bg-gray-100'
            }`}>
            {status}
        </div>
    )
}


const RenderRemarkForm = ({ onClick, formName }) => {
    const defaultValues = {};
    const dispatch = useDispatch();
    const formButtonLoading = useSelector((state) => state.loading.formButtonLoading)

    const submit = (formValues) => {
        const { processed_remark } = formValues;
        onClick({ processed_remark });
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    return (
        <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <span className="material-icons text-blue-600 text-font13">comment</span>
                    <h3 className="text-font13 font-semibold text-gray-800">Add Remark</h3>
                </div>
                <p className="text-font10 text-gray-600 mt-1">Please provide a detailed remark for this action</p>
            </div>

            <Form
                methods={methods}
                onSubmit={submit}
                submitButtonText={
                    <div className="flex items-center gap-2">
                        <span className="material-icons text-font10">send</span>
                        <span className="text-font10">Submit</span>
                    </div>
                }
                onCancel={() => dispatch(closeModalAction())}
                showSubmit
                submitContainerClassName='flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-200'
                isLoading={formButtonLoading[formName]}
            >
                <div className='px-6 py-6'>
                    <div className='bg-white rounded-xl border border-gray-200 p-4 shadow-sm transition-all duration-300 focus-within:shadow-md focus-within:border-blue-300'>
                        <TextArea
                            className='w-full border-0 focus:ring-0 resize-none'
                            name='processed_remark'
                            label={
                                <div className="flex items-center gap-2 mb-2">
                                    <span className="material-icons text-blue-600 text-font10">edit</span>
                                    <span className="text-font10 font-medium text-gray-700">Remark</span>
                                    <span className="text-red-500 text-font08">*</span>
                                </div>
                            }
                            placeholder="Enter your remark here..."
                            rows={4}
                        />
                    </div>
                </div>
            </Form>
        </div>
    );
}


export const RemarkForm = ({ onClick, onCancel, cancelButtonText = 'Cancel', submitContainerClassName = 'flex justify-end gap-3 p-4', formName, showClosingDate = false, askHaveYouUsedNewAsset = false, askHaveYouPickUpFaultyDevice = false, org_id, category, helpdesk_id }) => {
    const defaultValues = {};
    const dispatch = useDispatch();
    const { formButtonLoading } = useSelector((state) => state.loading)

    const submit = (formValues) => {
        const { processed_remark, closing_at, asset_consumed, faulty_device_picked_up } = formValues;
        onClick({ processed_remark, closing_at, asset_consumed, faulty_device_picked_up });
    };

    const methods = useForm({ defaultValues, mode: 'all' });

    return (
        <Form
            methods={methods}
            onSubmit={submit}
            submitButtonText='Submit'
            onCancel={typeof onCancel === 'function' ? onCancel : () => dispatch(closeModalAction())}
            cancelButtonText={cancelButtonText}
            showSubmit
            submitContainerClassName={submitContainerClassName}
            isLoading={formButtonLoading[formName]}
        >
            <div className='p-4 space-y-4'>
                <div className='bg-white duration-300'>
                    <div className='flex gap-2'>
                        <RenderRemarksInput
                            category={category}
                            org_id={org_id}
                            name='processed_remark'
                            label='Remark'
                            className='w-full focus:ring-2 focus:ring-primary/20 transition-all duration-300'
                            helpdesk_id={helpdesk_id}
                        />
                        {showClosingDate && (
                            <div className='animate-fadeIn w-full'>
                                <DatePicker
                                    name='closing_at'
                                    label='Closing Date'
                                    required
                                    className='w-full'
                                    maxDate={new Date()}
                                    withPortal={true}
                                />
                            </div>
                        )}
                    </div>

                    {askHaveYouUsedNewAsset && (
                        <div className='my-2 bg-blue-50 px-4 rounded-lg animate-fadeIn border border-blue-200 flex items-center'>
                            <Radio
                                name='asset_consumed'
                                label='Have you used the new asset?'
                                className='gap-3 flex'
                                options={[
                                    { label: 'Yes', value: 'yes' },
                                    { label: 'No', value: 'no' }
                                ]}
                            />
                        </div>
                    )}

                    {askHaveYouPickUpFaultyDevice && (
                        <div className='my-2 bg-amber-50 px-4 rounded-lg animate-fadeIn border border-amber-200'>
                            <Radio
                                name='faulty_device_picked_up'
                                label='Have you picked up the faulty device?'
                                className='gap-3 flex'
                                options={[
                                    { label: 'Yes', value: 'yes' },
                                    { label: 'No', value: 'no' }
                                ]}
                            />
                        </div>
                    )}
                    {(askHaveYouUsedNewAsset || askHaveYouPickUpFaultyDevice) && <p className='text-font08 text-amber-700 mt-1'>This information is important for inventory tracking</p>}
                </div>
            </div>
        </Form>
    );
};


const RenderTotalAmount = ({ tableSchema, arrayName, rowGenerator }) => {
    const { watch, setValue } = useFormContext();
    const payments = watch(arrayName) || [];

    return (
        <div className='bg-gradient-to-r from-blue-50 to-indigo-50 border-t-2 border-blue-200'>
            <div>
                <div className='flex w-full min-w-max flex-nowrap justify-start items-stretch gap-0'>
                    {tableSchema.table.map(({ head }, index) => {
                        let elem = <></>;
                        if (head.showTotal) {
                            let amount = 0;
                            payments.forEach(payment => {
                                if (payment.id === "BASE_PAYMENT") {
                                    if (head.role === TicketAgentRole.SP && head.milestone_id === payment.milestone_id) {
                                        amount += convertIntoRupee(payment.sp_base_payment);
                                    }
                                    if (head.role === TicketAgentRole.PSP) {
                                        amount += convertIntoRupee(payment.psp_incentive);
                                    }
                                    if (head.role === TicketAgentRole.CLUSTER) {
                                        amount += convertIntoRupee(payment.cluster_incentive);
                                    }
                                    if (head.role === TicketAgentRole.WAREHOUSE_OWNER) {
                                        amount += convertIntoRupee(payment.warehouse_incentive);
                                    }

                                    if (head.role === TicketAgentRole.CUSTOMER) {
                                        amount += convertIntoRupee(payment.customer_base_payment);
                                    }
                                }

                                if (head.role === TicketAgentRole.CUSTOMER && payment.purpose === TransactionPurpose.TICKET_BASE && !isNaN(payment.force_added_customer_base_payment)) {
                                    amount += convertIntoRupee(payment.force_added_customer_base_payment);
                                }

                                if (payment.roles && Object.keys(payment.roles).length > 0) {
                                    Object.entries(payment.roles).forEach(([key, { ticket_agent_role, net_credit, net_debit, credit, debit, purpose, amount_type, milestone_id, category }]) => {
                                        if (TicketAgentRole.SP === head.role) {
                                            const isExcluded = head.milestone_ids?.length > 0
                                                ? !head.milestone_ids.includes(milestone_id)
                                                : milestone_id !== head.milestone_id;
                                            if (isExcluded) return;
                                        }

                                        if (ticket_agent_role === head.role && purpose !== TransactionPurpose.TRANSFER && purpose !== TransactionPurpose.WITHDRAW && [TransactionStatus.INITIATED, TransactionStatus.PENDING, TransactionStatus.SCHEDULED, TransactionStatus.PROCESSING, TransactionStatus.SUCCESS].includes(payment.status)) {
                                            let a = getAmount({ net_credit, net_debit, credit, debit, purpose, amount_type, ticket_agent_role, category });
                                            amount += a;
                                        }
                                    });
                                }
                            });
                            elem = (
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-md border shadow-sm transition-all duration-200 ${amount < 0
                                    ? 'bg-red-50 border-red-200 text-red-700'
                                    : amount > 0
                                        ? 'bg-green-50 border-green-200 text-green-700'
                                        : 'bg-gray-50 border-gray-200 text-gray-700'
                                    }`}>
                                    <span className={`material-icons text-font08 ${amount < 0 ? 'text-red-600' : amount > 0 ? 'text-green-600' : 'text-gray-600'
                                        }`}>
                                        {amount < 0 ? 'trending_down' : amount > 0 ? 'trending_up' : 'trending_flat'}
                                    </span>
                                    <span className='font-medium text-font10'>₹ {Math.abs(amount).toFixed(2)}</span>
                                </div>
                            );
                        }
                        const footerMergedStyle = head.footerStyle && head.style ? { ...head.style, ...head.footerStyle } : head.style;
                        return (
                            <div key={head.headName} className={`${head.thClassName || ''} flex-shrink-0`} style={footerMergedStyle}>
                                {elem}
                                {index === 0 ? (
                                    <div className="flex-shrink-0 px-3">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const newRow = rowGenerator();
                                                const currentValues = watch(arrayName) || [];
                                                setValue(arrayName, [...currentValues, newRow]);
                                            }}
                                            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg text-font10 font-medium"
                                        >
                                            <span className="material-icons text-font10">add</span>
                                            <span>Add Payment</span>
                                        </button>
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
